import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
    Search,
    Bell,
    User,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    MapPin,
    Calendar,
    TrendingUp,
    TrendingDown,
    Download,
    Save,
    Filter,
    X,
    Loader2,
    AlertCircle,
    RefreshCw,
    ServerOff,
    Star,
    FileText,
    EyeOff,
    Eye,
} from 'lucide-react';
import { ChevronUpIcon, ChevronDownIcon } from './ChevronIcons';
import { MorphingLogo } from './MorphingLogo';
import { SettingsDrawer } from '@/components/RealSignal/SettingsDrawer';
import { PipelineBoard, type PipelineProperty, type PipelineStage } from './PipelineBoard';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import {
    realSignalApi,
    type MapPin as MapPinData,
    type ApiState,
    type DealCriteria,
    type CoverageMetadata,
    type EnrichmentState,
    type FieldEnrichmentStatus,
    type UserProfile,
    type ExecutionSignalsResponseV1,
    type TaxSignalsResponseV1,
    type InterestedPropertyDTO,
} from '../../services/realsignal-api';
import { exportToCSV, exportToPDF, type PropertyForPDF } from '../../utils/exportUtils';

const mapPinToProperty = (pin: MapPinData): Property => {
    const idParts = pin.propertyId.split(':');

    // Use real address from parcel data, fallback to property ID if not available
    const address = pin.address || `Property ${idParts[idParts.length - 1] || pin.propertyId}`;
    // Use county from API data directly (e.g., "Harris"), capitalize first letter
    // Fallback chain: API county -> ID segment -> 'Unknown' (truthful when data missing)
    const rawCounty = pin.county || idParts[1] || 'Unknown';
    const county = rawCounty.charAt(0).toUpperCase() + rawCounty.slice(1).toLowerCase();
    const city = pin.city || county;

    // Handle both camelCase and snake_case field names from API
    // Backend may return either format depending on deployment state
    const rawOpeningBid = pin.openingBid ?? pin.opening_bid;
    // Sanitize string values: strip currency symbols and commas, validate result
    const parseOpeningBid = (value: number | string | undefined): number | undefined => {
        if (value === undefined) return undefined;
        if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
        // Strip $ and , from string values before parsing
        const sanitized = value.replace(/[$,]/g, '');
        const parsed = parseFloat(sanitized);
        return Number.isFinite(parsed) ? parsed : undefined;
    };
    const parsedOpeningBid = parseOpeningBid(rawOpeningBid);
    const openingBidNum = parsedOpeningBid ?? 0;
    const auctionDate = pin.auctionDate ?? pin.auction_date;
    const caseFiledDate = (pin as any).caseFiledDate ?? (pin as any).case_filed_date;
    const locationConfidence = pin.locationConfidence ?? pin.location_confidence;

    // Opening bid is set only when we have a valid parsed value (not just raw presence)
    // This prevents showing $0 for invalid values like "TBD" or empty strings
    const hasOpeningBid = parsedOpeningBid !== undefined;
    // Comps/delta fields - only calculate if both opening bid and comps exist
    const hasCompsData = hasOpeningBid && pin.compsMedian !== undefined;
    const compsMedian = pin.compsMedian ?? 0;
    const delta = hasCompsData ? compsMedian - openingBidNum : 0;
    const deltaPercent =
        hasCompsData && compsMedian > 0 ? Math.round((delta / compsMedian) * 10000) / 100 : 0;

    return {
        id: pin.propertyId,
        parcelId: pin.parcelId,
        address,
        city,
        county,
        state: pin.state || 'TX',
        zip: pin.zip || '',
        // Parcel inventory fields
        ownerName: pin.ownerName,
        propertyType: pin.propertyType,
        assessedValue: pin.assessedValue,
        marketValue: pin.marketValue,
        yearBuilt: pin.yearBuilt,
        acreage: pin.acreage,
        // Foreclosure event fields
        status: pin.status
            ? pin.status === 'auction_scheduled'
                ? 'Scheduled'
                : pin.status === 'postponed'
                  ? 'Postponed'
                  : pin.status === 'cancelled'
                    ? 'Cancelled'
                    : undefined
            : undefined,
        locationConfidence: locationConfidence,
        // locationType from backend - no inference, direct mapping
        locationType: pin.locationType,
        // Opening bid is set whenever present (not gated by comps)
        openingBid: hasOpeningBid ? openingBidNum : undefined,
        // Comps/delta only set when comps data exists
        compsMedian: hasCompsData ? compsMedian : undefined,
        delta: hasCompsData ? delta : undefined,
        deltaPercent: hasCompsData ? deltaPercent : undefined,
        auctionDate: auctionDate,
        caseFiledDate: caseFiledDate,
        lat: pin.lat,
        lng: pin.lon,
        beds: pin.beds,
        baths: pin.baths,
        sqft: pin.sqft,
        criteriaMatches: pin.criteriaMatches || [],
    };
};

const colors = {
    background: '#0F1A24',
    backgroundLight: '#162434',
    backgroundCard: '#1C2E42',
    backgroundWell: '#0A1218',
    textPrimary: '#E6EBF0',
    textSecondary: '#A9B4C2',
    textMuted: '#7F8C9C',
    accent: '#3A6A7A',
    accentLight: '#4A8A9A',
    accentDark: '#2E4A5A',
    border: '#1C2E42',
    borderLight: '#2A3F54',
    success: '#22C55E',
    warning: '#F59E0B',
    error: '#EF4444',
};

// County configuration for Location Selector (Home Base Model)
// Only Harris County has data available; others are disabled with "Coming soon"
type CountyId = 'harris' | 'dallas' | 'tarrant' | 'bexar' | 'travis';

interface CountyConfig {
    name: string;
    enabled: boolean;
    center: { lat: number; lng: number };
    bbox: { minLon: number; minLat: number; maxLon: number; maxLat: number } | null;
    zoom: number;
}

const COUNTY_CONFIG: Record<CountyId, CountyConfig> = {
    harris: {
        name: 'Harris',
        enabled: true,
        center: { lat: 29.76, lng: -95.36 },
        bbox: { minLon: -95.96, minLat: 29.5, maxLon: -94.91, maxLat: 30.17 },
        zoom: 10,
    },
    dallas: {
        name: 'Dallas',
        enabled: false,
        center: { lat: 32.78, lng: -96.8 },
        bbox: null,
        zoom: 10,
    },
    tarrant: {
        name: 'Tarrant',
        enabled: false,
        center: { lat: 32.75, lng: -97.33 },
        bbox: null,
        zoom: 10,
    },
    bexar: {
        name: 'Bexar',
        enabled: false,
        center: { lat: 29.42, lng: -98.49 },
        bbox: null,
        zoom: 10,
    },
    travis: {
        name: 'Travis',
        enabled: false,
        center: { lat: 30.27, lng: -97.74 },
        bbox: null,
        zoom: 10,
    },
};

type LocationType = 'centroid' | 'parcel_polygon' | 'rooftop' | 'interpolated';

interface Property {
    id: string;
    parcelId?: string;
    address: string;
    city: string;
    county: string;
    state: string;
    zip: string;
    // Parcel inventory fields
    ownerName?: string;
    propertyType?: string;
    assessedValue?: number;
    marketValue?: number;
    yearBuilt?: number;
    acreage?: number;
    // Foreclosure event fields (undefined for parcel_inventory mode)
    status?: 'Scheduled' | 'Postponed' | 'Cancelled' | 'Sold';
    locationConfidence: 'high' | 'low';
    locationType: LocationType;
    openingBid?: number;
    compsMedian?: number;
    delta?: number;
    deltaPercent?: number;
    auctionDate?: string;
    caseFiledDate?: string;
    beds?: number;
    baths?: number;
    sqft?: number;
    lat: number;
    lng: number;
    criteriaMatches: string[];
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

const mapContainerStyle = {
    width: '100%',
    height: '100%',
};

// Dark theme matching sidebar aesthetic - colors derived from sidebar palette
// Sidebar: background=#0F1A24, backgroundLight=#162434, backgroundCard=#1C2E42, accent=#3A6A7A
const mapStyles = [
    // Base geometry - dark blue-gray matching sidebar backgroundCard
    { elementType: 'geometry', stylers: [{ color: '#1C2E42' }] },
    // Hide all labels by default
    { elementType: 'labels', stylers: [{ visibility: 'off' }] },
    // City/locality labels - light text on dark background
    {
        featureType: 'administrative.locality',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#A9B4C2' }, { visibility: 'on' }],
    },
    {
        featureType: 'administrative.locality',
        elementType: 'labels.text.stroke',
        stylers: [{ color: '#0F1A24' }, { visibility: 'on' }],
    },
    // Administrative boundaries - muted border color
    {
        featureType: 'administrative.country',
        elementType: 'geometry.stroke',
        stylers: [{ color: '#2A3F54' }],
    },
    {
        featureType: 'administrative.province',
        elementType: 'geometry.stroke',
        stylers: [{ color: '#2A3F54' }],
    },
    {
        featureType: 'administrative.neighborhood',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#7F8C9C' }, { visibility: 'on' }],
    },
    // Landscape - dark matching sidebar backgroundLight
    {
        featureType: 'landscape',
        elementType: 'geometry',
        stylers: [{ color: '#162434' }],
    },
    {
        featureType: 'landscape.man_made',
        elementType: 'geometry',
        stylers: [{ color: '#1C2E42' }],
    },
    // Natural landscape - slightly darker green-gray
    {
        featureType: 'landscape.natural',
        elementType: 'geometry',
        stylers: [{ color: '#1A2A38' }],
    },
    // POIs - all off for clean look
    {
        featureType: 'poi',
        stylers: [{ visibility: 'off' }],
    },
    // Parks - dark muted green
    {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [{ color: '#1A3028' }, { visibility: 'simplified' }],
    },
    // Roads - slightly lighter than background for visibility
    {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [{ color: '#2A3F54' }],
    },
    // Hide minor road labels
    {
        featureType: 'road',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
    },
    // Local roads - subtle visibility
    {
        featureType: 'road.local',
        elementType: 'geometry',
        stylers: [{ color: '#243648' }],
    },
    // Arterial roads - slightly more visible
    {
        featureType: 'road.arterial',
        elementType: 'geometry',
        stylers: [{ color: '#2A3F54' }],
    },
    // Highways - accent-tinted for visibility
    {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [{ color: '#3A5060' }],
    },
    // Highway labels - muted text
    {
        featureType: 'road.highway',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#7F8C9C' }, { visibility: 'on' }],
    },
    {
        featureType: 'road.highway',
        elementType: 'labels.text.stroke',
        stylers: [{ color: '#0F1A24' }, { visibility: 'on' }],
    },
    // Transit - off
    {
        featureType: 'transit',
        stylers: [{ visibility: 'off' }],
    },
    // Water - dark muted teal matching accent
    {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [{ color: '#2A4A5A' }],
    },
    {
        featureType: 'water',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#4A8A9A' }, { visibility: 'on' }],
    },
];

const mockProperties: Property[] = [
    {
        id: '1',
        address: '1234 Oak Street',
        city: 'Houston',
        county: 'Harris',
        state: 'TX',
        zip: '77001',
        status: 'Scheduled',
        locationConfidence: 'high',
        locationType: 'centroid',
        openingBid: 185000,
        compsMedian: 275000,
        delta: 90000,
        deltaPercent: 48.6,
        auctionDate: '2026-02-15',
        beds: 3,
        baths: 2,
        sqft: 1850,
        lat: 29.7604,
        lng: -95.3698,
        criteriaMatches: [],
    },
    {
        id: '2',
        address: '5678 Maple Avenue',
        city: 'Dallas',
        county: 'Dallas',
        state: 'TX',
        zip: '75201',
        status: 'Scheduled',
        locationConfidence: 'high',
        locationType: 'centroid',
        openingBid: 220000,
        compsMedian: 310000,
        delta: 90000,
        deltaPercent: 40.9,
        auctionDate: '2026-02-18',
        beds: 4,
        baths: 2.5,
        sqft: 2200,
        lat: 32.7767,
        lng: -96.797,
        criteriaMatches: [],
    },
    {
        id: '3',
        address: '910 Pine Road',
        city: 'Austin',
        county: 'Travis',
        state: 'TX',
        zip: '78701',
        status: 'Postponed',
        locationConfidence: 'low',
        locationType: 'interpolated',
        openingBid: 165000,
        compsMedian: 240000,
        delta: 75000,
        deltaPercent: 45.5,
        auctionDate: '2026-02-20',
        beds: 3,
        baths: 2,
        sqft: 1650,
        lat: 30.2672,
        lng: -97.7431,
        criteriaMatches: [],
    },
    {
        id: '4',
        address: '2468 Elm Drive',
        city: 'San Antonio',
        county: 'Bexar',
        state: 'TX',
        zip: '78201',
        status: 'Scheduled',
        locationConfidence: 'high',
        locationType: 'centroid',
        openingBid: 145000,
        compsMedian: 195000,
        delta: 50000,
        deltaPercent: 34.5,
        auctionDate: '2026-02-22',
        beds: 2,
        baths: 1,
        sqft: 1200,
        lat: 29.4241,
        lng: -98.4936,
        criteriaMatches: [],
    },
    {
        id: '5',
        address: '1357 Cedar Lane',
        city: 'Fort Worth',
        county: 'Tarrant',
        state: 'TX',
        zip: '76101',
        status: 'Scheduled',
        locationConfidence: 'low',
        locationType: 'interpolated',
        openingBid: 198000,
        compsMedian: 285000,
        delta: 87000,
        deltaPercent: 43.9,
        auctionDate: '2026-02-25',
        beds: 3,
        baths: 2,
        sqft: 1900,
        lat: 32.7555,
        lng: -97.3308,
        criteriaMatches: [],
    },
];

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Generate colored pin SVG data URL matching the user's custom pin design
const createColoredPinSvg = (fillColor: string, strokeColor: string = '#ffffff'): string => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <path d="M24 4C16.27 4 10 10.27 10 18c0 10.5 14 26 14 26s14-15.5 14-26c0-7.73-6.27-14-14-14z" fill="${fillColor}" stroke="${strokeColor}" stroke-width="3"/>
        <circle cx="24" cy="18" r="5" fill="${strokeColor}"/>
    </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// Normalize property type strings to filter categories
const normalizePropertyType = (type: string): string => {
    const lower = type.toLowerCase();
    if (lower.includes('single') || lower.includes('sfh') || lower.includes('house')) return 'SFH';
    if (lower.includes('condo') || lower.includes('townhouse') || lower.includes('town'))
        return 'Condo';
    if (
        lower.includes('multi') ||
        lower.includes('duplex') ||
        lower.includes('triplex') ||
        lower.includes('fourplex')
    )
        return 'Multi';
    if (lower.includes('lot') || lower.includes('land') || lower.includes('vacant')) return 'Lot';
    return 'Other';
};

interface DashboardProps {
    onLogout?: () => void;
    onNavigateToAlerts?: () => void;
    onNavigateToSavedSearches?: () => void;
    onRequestLogin?: (pendingPropertyId?: string) => void;
    userProfile?: UserProfile | null;
    pendingStarPropertyId?: string | null;
    onPendingStarComplete?: () => void;
}

interface SearchedLocation {
    lat: number;
    lng: number;
    address: string;
}

export const Dashboard: React.FC<DashboardProps> = ({
    onLogout: _onLogout,
    onNavigateToAlerts,
    onNavigateToSavedSearches: _onNavigateToSavedSearches,
    onRequestLogin,
    userProfile: userProfileProp,
    pendingStarPropertyId,
    onPendingStarComplete,
}) => {
    const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
    const [includeApproximate, setIncludeApproximate] = useState(false);
    const [showFilters, setShowFilters] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const [hasInitialFit, setHasInitialFit] = useState(false);
    const [searchedLocation, setSearchedLocation] = useState<SearchedLocation | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [showSettings, setShowSettings] = useState(false);

    const [properties, setProperties] = useState<Property[]>([]);
    const [apiState, setApiState] = useState<ApiState>('loading');
    const [apiError, setApiError] = useState<string | null>(null);
    const [coverage, setCoverage] = useState<CoverageMetadata | null>(null);

    // Deal Criteria state - V1 UI Fix #4: Two-phase filtering
    // draftCriteria: updates as user types (not applied yet)
    // appliedCriteria: changes only when Apply or Enter is triggered
    // This prevents pins from flickering while typing in numeric fields
    const [draftCriteria, setDraftCriteria] = useState<DealCriteria>({});
    const [appliedCriteria, setAppliedCriteria] = useState<DealCriteria>({});

    // Location Selector state - Home Base Model (single county selection)
    // Harris is the default and only enabled county for V1
    const [selectedCounty, setSelectedCounty] = useState<CountyId>('harris');

    // Beds & Baths filter state - client-side filtering (V1 UI Fix #2)
    // These filters apply to both map pins and property list in lockstep
    const [bedsMin, setBedsMin] = useState<number | undefined>(undefined);
    const [bedsMax, setBedsMax] = useState<number | undefined>(undefined);
    const [bathsMin, setBathsMin] = useState<number | undefined>(undefined);
    const [bathsMax, setBathsMax] = useState<number | undefined>(undefined);

    // Sq Ft filter state - V1 Core physical viability filter
    const [sqftMin, setSqftMin] = useState<number | undefined>(undefined);
    const [sqftMax, setSqftMax] = useState<number | undefined>(undefined);

    // Property Type filter state - V1 Core physical viability filter
    const ALL_PROPERTY_TYPES = ['SFH', 'Condo', 'Multi', 'Lot', 'Other'] as const;
    type PropertyTypeFilter = (typeof ALL_PROPERTY_TYPES)[number];
    const [selectedPropertyTypes, setSelectedPropertyTypes] = useState<Set<PropertyTypeFilter>>(
        new Set()
    );

    // Foreclosure Status filter state - client-side filtering (V1 UI Fix #3)
    // Uses Set for O(1) lookup; empty set or all 4 selected = "show all"
    const ALL_STATUSES = ['Scheduled', 'Postponed', 'Cancelled', 'Sold'] as const;
    type StatusType = (typeof ALL_STATUSES)[number];
    const [selectedStatuses, setSelectedStatuses] = useState<Set<StatusType>>(new Set());

    // Sorting state for results panel
    type SortOption = 'auction_date' | 'opening_bid_asc' | 'opening_bid_desc';
    const [sortBy, setSortBy] = useState<SortOption>('auction_date');

    // Pin flash animation state - tracks which property pin is currently flashing
    const [flashingPinId, setFlashingPinId] = useState<string | null>(null);

    // Pagination state for results panel
    const CARDS_PER_PAGE = 6;
    const [currentPage, setCurrentPage] = useState(1);

    // Watchlist/Interested state - tracks which properties user has starred
    const [interestedIds, setInterestedIds] = useState<Set<string>>(new Set());
    // Interested properties with canonical DTOs (Phase 5B.2D)
    const [interestedPropertiesData, setInterestedPropertiesData] = useState<
        Map<string, InterestedPropertyDTO>
    >(new Map());
    const [showInterestedOnly, setShowInterestedOnly] = useState(() => {
        const saved = localStorage.getItem('realsignal_showInterestedOnly');
        return saved === 'true';
    });

    // Dismissed state - tracks which properties user has dismissed
    const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
    const [showDismissedOnly, setShowDismissedOnly] = useState(false);

    // View mode state - toggle between Map view and Pipeline Board view
    type ViewMode = 'map' | 'pipeline';
    const [viewMode, setViewMode] = useState<ViewMode>('map');

    // Enrichment state - tracks field enrichment status for selected property
    // Key: propertyId, Value: Record of field name to enrichment status
    const [enrichmentCache, setEnrichmentCache] = useState<
        Record<string, Record<string, FieldEnrichmentStatus>>
    >({});
    const [enrichmentLoading, setEnrichmentLoading] = useState(false);
    const [enrichmentRequesting, setEnrichmentRequesting] = useState(false);
    // Track whether enrichment system is initialized (tables exist)
    const [enrichmentTablesInitialized, setEnrichmentTablesInitialized] = useState<boolean | null>(
        null
    );

    // User profile is now passed from app root (single source of truth)
    // Use prop directly instead of local state to avoid split-brain auth
    const userProfile = userProfileProp ?? null;

    // Get userId from JWT token (set during login)
    // Returns null if not authenticated - callers must handle this case
    const getUserId = useCallback((): string | null => {
        return realSignalApi.getCurrentUserId();
    }, []);

    // Fetch interested property IDs and full canonical DTOs on mount
    // Phase 5B.2D-3: Eager pagination - loads all pages to support >500 interested properties
    const fetchInterestedIds = useCallback(async () => {
        const userId = getUserId();
        if (!userId) {
            // Not authenticated - clear interested IDs and data
            setInterestedIds(new Set());
            setInterestedPropertiesData(new Map());
            return;
        }
        // Fetch IDs for quick lookup
        const idsResult = await realSignalApi.getInterestedIds(userId);
        if (idsResult.state === 'success' && idsResult.data) {
            setInterestedIds(new Set(idsResult.data));
        }
        // Fetch full interested properties with canonical DTOs (Phase 5B.2D)
        // Eager pagination: loop through all pages until we have all items
        const PAGE_SIZE = 500;
        const MAX_PAGES = 20; // Safety limit: 10,000 properties max
        const dataMap = new Map<string, InterestedPropertyDTO>();
        let offset = 0;
        let pageCount = 0;

        while (pageCount < MAX_PAGES) {
            const propsResult = await realSignalApi.getInterestedProperties(
                userId,
                PAGE_SIZE,
                offset
            );
            if (propsResult.state !== 'success' || !propsResult.data) {
                break;
            }
            for (const item of propsResult.data.items) {
                dataMap.set(item.propertyId, item);
            }
            // Check if we've loaded all items
            // Note: Don't use items.length < PAGE_SIZE as termination condition because
            // backend may filter out records with missing property details, returning
            // fewer items than PAGE_SIZE even when more pages exist.
            // Instead, use offset-based check against total.
            const { total, items } = propsResult.data;
            offset += PAGE_SIZE;
            pageCount++;
            // Stop if we've requested past the total count or got an empty page
            if (offset >= total || items.length === 0) {
                break;
            }
        }
        setInterestedPropertiesData(dataMap);
    }, [getUserId]);

    const fetchDismissedIds = useCallback(async () => {
        const userId = getUserId();
        if (!userId) {
            setDismissedIds(new Set());
            return;
        }
        const result = await realSignalApi.getDismissedIds();
        if (result.state === 'success' && result.data) {
            setDismissedIds(new Set(result.data));
        } else {
            setDismissedIds(new Set());
        }
    }, [getUserId]);

    // Fetch enrichment state for a property
    const fetchEnrichmentState = useCallback(
        async (propertyId: string) => {
            // Check cache first
            if (enrichmentCache[propertyId]) {
                return enrichmentCache[propertyId];
            }

            setEnrichmentLoading(true);
            try {
                const result = await realSignalApi.getPropertyEnrichment(propertyId);
                if (result.state === 'success' && result.data) {
                    // Store tablesInitialized state
                    setEnrichmentTablesInitialized(result.data.tablesInitialized);
                    setEnrichmentCache(prev => ({
                        ...prev,
                        [propertyId]: result.data.fields,
                    }));
                    return result.data.fields;
                }
            } catch (error) {
                console.error('[Enrichment] Failed to fetch enrichment state:', error);
            } finally {
                setEnrichmentLoading(false);
            }
            return null;
        },
        [enrichmentCache]
    );

    // Request enrichment for a property
    const requestEnrichment = useCallback(async (propertyId: string) => {
        setEnrichmentRequesting(true);
        try {
            const result = await realSignalApi.requestPropertyEnrichment(propertyId);
            if (result.state === 'success' && result.data) {
                // Store tablesInitialized state
                setEnrichmentTablesInitialized(result.data.tablesInitialized);
                // Update cache with new field states
                setEnrichmentCache(prev => ({
                    ...prev,
                    [propertyId]: result.data.fields,
                }));
                return result.data;
            }
        } catch (error) {
            console.error('[Enrichment] Failed to request enrichment:', error);
        } finally {
            setEnrichmentRequesting(false);
        }
        return null;
    }, []);

    // Star/unstar a property
    const handleToggleStar = useCallback(
        async (propertyId: string, e?: React.MouseEvent) => {
            if (e) {
                e.stopPropagation();
            }
            const userId = getUserId();
            if (!userId) {
                // Not authenticated - redirect to login with pending action
                console.warn('[Watchlist] Please sign in to save properties to your watchlist');
                if (onRequestLogin) {
                    onRequestLogin(propertyId); // Pass propertyId for post-login continuation
                }
                return;
            }
            const isCurrentlyStarred = interestedIds.has(propertyId);

            // Optimistic update
            setInterestedIds(prev => {
                const next = new Set(prev);
                if (isCurrentlyStarred) {
                    next.delete(propertyId);
                } else {
                    next.add(propertyId);
                }
                return next;
            });

            // API call
            if (isCurrentlyStarred) {
                const result = await realSignalApi.unstarProperty(userId, propertyId);
                if (result.state === 'authError') {
                    // Session expired - revert and redirect to login
                    setInterestedIds(prev => new Set([...prev, propertyId]));
                    console.warn('[Watchlist] Session expired:', result.error);
                    if (onRequestLogin) {
                        onRequestLogin();
                    }
                } else if (result.state !== 'success') {
                    // Revert on failure
                    setInterestedIds(prev => new Set([...prev, propertyId]));
                    console.error('[Watchlist] Failed to unstar property:', result.error);
                }
            } else {
                const result = await realSignalApi.starProperty(userId, propertyId);
                if (result.state === 'authError') {
                    // Session expired - revert and redirect to login
                    setInterestedIds(prev => {
                        const next = new Set(prev);
                        next.delete(propertyId);
                        return next;
                    });
                    console.warn('[Watchlist] Session expired:', result.error);
                    if (onRequestLogin) {
                        onRequestLogin();
                    }
                } else if (result.state !== 'success') {
                    // Revert on failure
                    setInterestedIds(prev => {
                        const next = new Set(prev);
                        next.delete(propertyId);
                        return next;
                    });
                    console.error('[Watchlist] Failed to star property:', result.error);
                }
            }
        },
        [getUserId, interestedIds, onRequestLogin]
    );

    const handleDismiss = useCallback(
        async (propertyId: string, e?: React.MouseEvent) => {
            if (e) {
                e.stopPropagation();
            }
            if (!getUserId()) {
                if (onRequestLogin) {
                    onRequestLogin();
                }
                return;
            }
            setDismissedIds(prev => {
                const next = new Set(prev);
                next.add(propertyId);
                return next;
            });
            const result = await realSignalApi.dismissProperty(propertyId);
            if (result.state === 'authError') {
                setDismissedIds(prev => {
                    const next = new Set(prev);
                    next.delete(propertyId);
                    return next;
                });
                console.warn('[Dismiss] Session expired:', result.error);
                if (onRequestLogin) {
                    onRequestLogin();
                }
            } else if (result.state !== 'success') {
                setDismissedIds(prev => {
                    const next = new Set(prev);
                    next.delete(propertyId);
                    return next;
                });
                console.error('[Dismiss] Failed to dismiss property:', result.error);
            }
        },
        [getUserId, onRequestLogin]
    );

    const handleRestore = useCallback(
        async (propertyId: string, e?: React.MouseEvent) => {
            if (e) {
                e.stopPropagation();
            }
            if (!getUserId()) {
                if (onRequestLogin) {
                    onRequestLogin();
                }
                return;
            }
            setDismissedIds(prev => {
                const next = new Set(prev);
                next.delete(propertyId);
                return next;
            });
            const result = await realSignalApi.restoreProperty(propertyId);
            if (result.state === 'authError') {
                setDismissedIds(prev => {
                    const next = new Set(prev);
                    next.add(propertyId);
                    return next;
                });
                console.warn('[Dismiss] Session expired:', result.error);
                if (onRequestLogin) {
                    onRequestLogin();
                }
            } else if (result.state !== 'success') {
                setDismissedIds(prev => {
                    const next = new Set(prev);
                    next.add(propertyId);
                    return next;
                });
                console.error('[Dismiss] Failed to restore property:', result.error);
            }
        },
        [getUserId, onRequestLogin]
    );

    // Persist showInterestedOnly toggle to localStorage
    useEffect(() => {
        localStorage.setItem('realsignal_showInterestedOnly', showInterestedOnly.toString());
    }, [showInterestedOnly]);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    });

    const fetchMapPins = useCallback(
        async (manualRefresh = false) => {
            if (manualRefresh) {
                realSignalApi.clearNotDeployedState('/api/realsignal/map-pins');
                realSignalApi.clearCache();
            }

            setApiState('loading');
            setApiError(null);
            setCoverage(null);

            // V1 UI Fix #4: No longer pass criteria to backend - all filtering is client-side
            const result = await realSignalApi.getMapPins({
                bbox: '-95.9,29.5,-94.9,30.2',
                includeApproximate,
                limit: 200,
            });

            setApiState(result.state);
            setApiError(result.error);

            if (result.state === 'success' && result.data) {
                setCoverage(result.data.coverage || null);
                const mappedProperties = result.data.pins.map(mapPinToProperty);
                setProperties(mappedProperties);
            } else if (result.state === 'empty') {
                if (result.data?.coverage) {
                    setCoverage(result.data.coverage);
                }
                setProperties([]);
            } else if (result.state === 'notDeployed' || result.state === 'error') {
                setProperties(mockProperties);
            }
        },
        [includeApproximate]
    );

    useEffect(() => {
        fetchMapPins();
    }, [fetchMapPins]);

    // Initialize auth and fetch interested property IDs on mount
    // User profile is now passed from app root (single source of truth)
    useEffect(() => {
        const initializeAuth = async () => {
            // Ensure we have a valid auth token (will initialize demo session if needed)
            await realSignalApi.ensureAuth();
            // Now fetch interested IDs and dismissed IDs with valid auth
            fetchInterestedIds();
            fetchDismissedIds();
        };
        initializeAuth();
    }, [fetchInterestedIds, fetchDismissedIds]);

    // Handle pending star action after login (post-login continuation)
    // This completes the watchlist star action that was interrupted by login
    useEffect(() => {
        if (pendingStarPropertyId && realSignalApi.isAuthenticated()) {
            console.log('[Watchlist] Completing pending star action for:', pendingStarPropertyId);
            handleToggleStar(pendingStarPropertyId);
            if (onPendingStarComplete) {
                onPendingStarComplete();
            }
        }
    }, [pendingStarPropertyId, handleToggleStar, onPendingStarComplete]);

    const handleCheckAgain = useCallback(() => {
        fetchMapPins(true);
    }, [fetchMapPins]);

    // Derived filteredProperties array - single source of truth for map pins AND property list
    // Applies: location confidence filter + beds/baths client-side filters
    // Both map and list MUST render from this same array to stay in sync
    const filteredProperties = useMemo(() => {
        let result = includeApproximate
            ? properties
            : properties.filter(p => p.locationConfidence === 'high');

        // Apply beds filter (client-side, no backend request)
        if (bedsMin !== undefined) {
            result = result.filter(p => {
                if (p.beds === undefined || p.beds === null) return false;
                return p.beds >= bedsMin;
            });
        }
        if (bedsMax !== undefined) {
            result = result.filter(p => {
                if (p.beds === undefined || p.beds === null) return false;
                return p.beds <= bedsMax;
            });
        }

        // Apply baths filter (client-side, no backend request)
        if (bathsMin !== undefined) {
            result = result.filter(p => {
                if (p.baths === undefined || p.baths === null) return false;
                return p.baths >= bathsMin;
            });
        }
        if (bathsMax !== undefined) {
            result = result.filter(p => {
                if (p.baths === undefined || p.baths === null) return false;
                return p.baths <= bathsMax;
            });
        }

        // Apply sqft filter (client-side, V1 Core physical viability)
        if (sqftMin !== undefined) {
            result = result.filter(p => {
                if (p.sqft === undefined || p.sqft === null) return false;
                return p.sqft >= sqftMin;
            });
        }
        if (sqftMax !== undefined) {
            result = result.filter(p => {
                if (p.sqft === undefined || p.sqft === null) return false;
                return p.sqft <= sqftMax;
            });
        }

        // Apply property type filter (client-side, V1 Core physical viability)
        // OR logic: show properties matching ANY selected type
        // "Show all" when: none selected OR all types selected
        const isPropertyTypeFilterActive =
            selectedPropertyTypes.size > 0 &&
            selectedPropertyTypes.size < ALL_PROPERTY_TYPES.length;
        if (isPropertyTypeFilterActive) {
            result = result.filter(p => {
                if (!p.propertyType) return false;
                // Normalize property type to match filter options
                const normalizedType = normalizePropertyType(p.propertyType);
                return selectedPropertyTypes.has(normalizedType as PropertyTypeFilter);
            });
        }

        // Apply status filter (client-side, no backend request) - V1 UI Fix #3
        // OR logic: show properties matching ANY selected status
        // "Show all" when: none selected OR all 4 selected
        const isStatusFilterActive =
            selectedStatuses.size > 0 && selectedStatuses.size < ALL_STATUSES.length;
        if (isStatusFilterActive) {
            result = result.filter(p => {
                // Exclude properties with missing/unknown status when filter is active
                if (!p.status) return false;
                return selectedStatuses.has(p.status as StatusType);
            });
        }

        // Apply Deal Criteria filters (client-side, V1 UI Fix #4)
        // These use appliedCriteria (committed on Apply/Enter), not draftCriteria
        if (appliedCriteria.maxOpeningBid !== undefined) {
            result = result.filter(p => p.openingBid <= appliedCriteria.maxOpeningBid!);
        }
        if (appliedCriteria.minOpeningBid !== undefined) {
            result = result.filter(p => p.openingBid >= appliedCriteria.minOpeningBid!);
        }
        if (appliedCriteria.minDelta !== undefined) {
            result = result.filter(p => p.delta >= appliedCriteria.minDelta!);
        }
        if (appliedCriteria.minDeltaPercent !== undefined) {
            result = result.filter(p => p.deltaPercent >= appliedCriteria.minDeltaPercent!);
        }
        if (appliedCriteria.auctionWithinDays !== undefined) {
            const now = new Date();
            const cutoffDate = new Date(
                now.getTime() + appliedCriteria.auctionWithinDays * 24 * 60 * 60 * 1000
            );
            result = result.filter(p => {
                if (!p.auctionDate) return false;
                const auctionDate = new Date(p.auctionDate);
                return auctionDate <= cutoffDate && auctionDate >= now;
            });
        }
        if (appliedCriteria.hasComps === true) {
            result = result.filter(p => p.compsMedian > 0);
        }

        // Apply Interested Only filter - show only starred properties
        if (showInterestedOnly) {
            result = result.filter(p => interestedIds.has(p.id));
        }

        // Apply Dismissed filter
        if (showDismissedOnly) {
            result = result.filter(p => dismissedIds.has(p.id));
        } else {
            result = result.filter(p => !dismissedIds.has(p.id) || interestedIds.has(p.id));
        }

        return result;
    }, [
        properties,
        includeApproximate,
        bedsMin,
        bedsMax,
        bathsMin,
        bathsMax,
        sqftMin,
        sqftMax,
        selectedPropertyTypes,
        selectedStatuses,
        appliedCriteria,
        showInterestedOnly,
        interestedIds,
        showDismissedOnly,
        dismissedIds,
    ]);

    // Sorted properties for the results panel - applies sorting to filtered results
    const sortedProperties = useMemo(() => {
        const sorted = [...filteredProperties];
        switch (sortBy) {
            case 'auction_date':
                return sorted.sort((a, b) => {
                    if (!a.auctionDate && !b.auctionDate) return 0;
                    if (!a.auctionDate) return 1;
                    if (!b.auctionDate) return -1;
                    return new Date(a.auctionDate).getTime() - new Date(b.auctionDate).getTime();
                });
            case 'opening_bid_asc':
                return sorted.sort((a, b) => (a.openingBid || 0) - (b.openingBid || 0));
            case 'opening_bid_desc':
                return sorted.sort((a, b) => (b.openingBid || 0) - (a.openingBid || 0));
            default:
                return sorted;
        }
    }, [filteredProperties, sortBy]);

    // Pagination calculations for results panel
    const totalPages = Math.ceil(sortedProperties.length / CARDS_PER_PAGE);
    const paginatedProperties = useMemo(() => {
        const startIndex = (currentPage - 1) * CARDS_PER_PAGE;
        return sortedProperties.slice(startIndex, startIndex + CARDS_PER_PAGE);
    }, [sortedProperties, currentPage, CARDS_PER_PAGE]);

    // Reset page to 1 when filters, sort, or data changes
    // Note: We depend on `properties` (not just length) to reset on data refresh
    // even when the count stays the same (e.g., API returns different pins)
    useEffect(() => {
        setCurrentPage(1);
    }, [
        properties,
        sortBy,
        includeApproximate,
        bedsMin,
        bedsMax,
        bathsMin,
        bathsMax,
        selectedStatuses,
        appliedCriteria,
    ]);

    // Check if any beds/baths filters are active (for visual indicator)
    const hasActiveBedsOrBathsFilters =
        bedsMin !== undefined ||
        bedsMax !== undefined ||
        bathsMin !== undefined ||
        bathsMax !== undefined;

    // Check if status filter is active (subset selected, not "show all")
    // "Show all" = none selected OR all 4 selected (no scary filter badge)
    const hasActiveStatusFilter =
        selectedStatuses.size > 0 && selectedStatuses.size < ALL_STATUSES.length;

    // Check if any Deal Criteria filters are applied (for visual indicator)
    const hasAppliedCriteria =
        appliedCriteria.maxOpeningBid !== undefined ||
        appliedCriteria.minOpeningBid !== undefined ||
        appliedCriteria.minDelta !== undefined ||
        appliedCriteria.minDeltaPercent !== undefined ||
        appliedCriteria.auctionWithinDays !== undefined ||
        appliedCriteria.hasComps !== undefined;

    // Handler to apply draft criteria (called on Apply button or Enter key)
    const handleApplyCriteria = useCallback(() => {
        setAppliedCriteria({ ...draftCriteria });
    }, [draftCriteria]);

    // Handler for Enter key in Apply-gated inputs
    const handleCriteriaKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleApplyCriteria();
            }
        },
        [handleApplyCriteria]
    );

    // Derive selectedProperty from filteredProperties to ensure selection clears
    // when filters exclude the selected property (prevents stale drawer state)
    const selectedProperty = filteredProperties.find(p => p.id === selectedPropertyId);

    const initialCenter = useMemo(() => {
        // Always use the selected county's center for deterministic initial positioning
        // This ensures the map starts at Houston (Harris County) regardless of data state
        // The onLoad callback will then call fitBounds to properly fit the county bounds
        const config = COUNTY_CONFIG[selectedCounty];
        return config.center;
    }, [selectedCounty]);

    // Load persisted map position from localStorage on mount
    const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(() => {
        try {
            const saved = localStorage.getItem('realsignal_map_center');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed.lat === 'number' && typeof parsed.lng === 'number') {
                    return parsed;
                }
            }
        } catch {
            // Ignore localStorage errors
        }
        return null;
    });
    const [mapZoom, setMapZoom] = useState<number | null>(() => {
        try {
            const saved = localStorage.getItem('realsignal_map_zoom');
            if (saved) {
                const parsed = Number(saved);
                if (!isNaN(parsed) && parsed >= 0 && parsed <= 22) {
                    return parsed;
                }
            }
        } catch {
            // Ignore localStorage errors
        }
        return null;
    });
    // Default zoom level matches Harris County config for consistent initial view
    const [currentZoom, setCurrentZoom] = useState<number>(COUNTY_CONFIG[selectedCounty].zoom);

    // Handler to save map position to localStorage (called on map idle)
    const handleMapIdle = useCallback(() => {
        if (mapRef.current) {
            const center = mapRef.current.getCenter();
            const zoom = mapRef.current.getZoom();
            if (center && zoom !== undefined) {
                const newCenter = { lat: center.lat(), lng: center.lng() };
                try {
                    localStorage.setItem('realsignal_map_center', JSON.stringify(newCenter));
                    localStorage.setItem('realsignal_map_zoom', String(zoom));
                } catch {
                    // Ignore localStorage errors (e.g., quota exceeded)
                }
                setMapCenter(newCenter);
                setMapZoom(zoom);
            }
        }
    }, []);

    // Center map to selected county's bounding box
    const centerMapToCounty = useCallback(
        (countyId: CountyId, mapInstance?: google.maps.Map | null) => {
            const targetMap = mapInstance || map;
            if (!targetMap) return;

            const config = COUNTY_CONFIG[countyId];
            if (config.bbox) {
                const bounds = new google.maps.LatLngBounds(
                    { lat: config.bbox.minLat, lng: config.bbox.minLon },
                    { lat: config.bbox.maxLat, lng: config.bbox.maxLon }
                );
                targetMap.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
            } else {
                targetMap.setCenter(config.center);
                targetMap.setZoom(config.zoom);
            }
        },
        [map]
    );

    // Handle county selection (single-select, home base model)
    const handleCountySelect = useCallback(
        (countyId: CountyId) => {
            const config = COUNTY_CONFIG[countyId];
            // Only allow selection of enabled counties
            if (!config.enabled) return;

            setSelectedCounty(countyId);
            centerMapToCounty(countyId);
        },
        [centerMapToCounty]
    );

    const onLoad = useCallback(
        (mapInstance: google.maps.Map) => {
            setMap(mapInstance);
            mapRef.current = mapInstance;

            // Skip initial county fit if we have a persisted map position
            // This preserves the user's last viewed location across navigation
            if (!hasInitialFit && !mapCenter && !mapZoom) {
                centerMapToCounty(selectedCounty, mapInstance);
                setHasInitialFit(true);
            }
        },
        [hasInitialFit, selectedCounty, centerMapToCounty, mapCenter, mapZoom]
    );

    const onUnmount = useCallback(() => {
        setMap(null);
    }, []);

    const handleZoomIn = useCallback(() => {
        if (map) {
            const currentZoom = map.getZoom() || 6;
            map.setZoom(currentZoom + 1);
        }
    }, [map]);

    const handleZoomOut = useCallback(() => {
        if (map) {
            const currentZoom = map.getZoom() || 6;
            map.setZoom(currentZoom - 1);
        }
    }, [map]);

    const handlePinClick = useCallback(
        (property: Property) => {
            setSelectedPropertyId(property.id);
            if (map) {
                map.panTo({ lat: property.lat, lng: property.lng });
                map.setZoom(12);
            }
            // Fetch enrichment state for the selected property
            void fetchEnrichmentState(property.id);
        },
        [map, fetchEnrichmentState]
    );

    // Handler for card click - pans map to property and triggers pin flash animation
    const handleCardClick = useCallback(
        (property: Property) => {
            setSelectedPropertyId(property.id);
            if (map) {
                // Pan map to center on the selected property's pin
                map.panTo({ lat: property.lat, lng: property.lng });
                // Zoom in to show the property clearly
                const currentZoom = map.getZoom() || 10;
                if (currentZoom < 14) {
                    map.setZoom(14);
                }
            }
            // Trigger pin flash animation
            setFlashingPinId(property.id);
            // Clear flash after animation completes (600ms for 3 flashes at 200ms each)
            setTimeout(() => {
                setFlashingPinId(null);
            }, 600);
            // Fetch enrichment state for the selected property
            void fetchEnrichmentState(property.id);
        },
        [map, fetchEnrichmentState]
    );

    const handleSearch = useCallback(() => {
        if (!searchQuery.trim()) {
            return;
        }
        if (!window.google) {
            return;
        }
        if (!mapRef.current) {
            return;
        }

        setIsSearching(true);
        setSelectedPropertyId(null);

        const geocoder = new google.maps.Geocoder();

        geocoder.geocode({ address: searchQuery }, (results, status) => {
            setIsSearching(false);

            if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
                const location = results[0].geometry.location;
                const lat = location.lat();
                const lng = location.lng();

                setSearchedLocation({
                    lat,
                    lng,
                    address: results[0].formatted_address,
                });

                setMapCenter({ lat, lng });
                setMapZoom(17);
            } else {
                setSearchedLocation(null);
            }
        });
    }, [searchQuery]);

    const handleSearchKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                handleSearch();
            }
        },
        [handleSearch]
    );

    const handleExportCSV = useCallback(() => {
        const propertiesToExport = showInterestedOnly
            ? filteredProperties
            : filteredProperties.filter(p => interestedIds.has(p.id));

        if (propertiesToExport.length === 0) {
            return;
        }

        // Helper to sanitize values: no NaN, no undefined, no null
        // Use 'TBD' only for genuinely unknown values where enrichment hasn't produced data
        const sanitize = (value: string | number | undefined | null, fallback = ''): string => {
            if (value === undefined || value === null) return fallback;
            if (typeof value === 'number') {
                if (!Number.isFinite(value)) return fallback;
                return value.toString();
            }
            const str = String(value).trim();
            if (str === '' || str === 'undefined' || str === 'null' || str === 'NaN') {
                return fallback;
            }
            return str;
        };

        // Derive owner type from owner name (individual vs entity heuristic)
        const deriveOwnerType = (ownerName: string | undefined): string => {
            if (!ownerName) return 'TBD';
            const name = ownerName.toUpperCase();
            const entityIndicators = [
                'LLC',
                'INC',
                'CORP',
                'LP',
                'LTD',
                'TRUST',
                'BANK',
                'PROPERTIES',
                'INVESTMENTS',
                'HOLDINGS',
                'PARTNERS',
                'COMPANY',
                'CO.',
                'ASSOCIATION',
                'FOUNDATION',
            ];
            for (const indicator of entityIndicators) {
                if (name.includes(indicator)) return 'Entity';
            }
            return 'Individual';
        };

        // Map location confidence to quality label
        const getLocationQuality = (
            confidence: string | undefined,
            locationType: string | undefined
        ): string => {
            if (locationType === 'rooftop') return 'Rooftop';
            if (locationType === 'parcel_polygon') return 'Parcel';
            if (confidence === 'high') return 'High';
            if (confidence === 'low') return 'Approximate';
            return 'TBD';
        };

        // Generate HCAD URL from property ID
        const getHcadUrl = (propertyId: string): string => {
            // Property ID format: realsignal:harris:ACCOUNT_NUMBER
            const parts = propertyId.split(':');
            const accountNumber = parts[parts.length - 1];
            if (accountNumber && /^\d+$/.test(accountNumber)) {
                return `https://hcad.org/property-search/real-property/real-property-search/?account=${accountNumber}`;
            }
            return '';
        };

        // Investor-ready CSV headers (snake_case for data interchange)
        const headers = [
            'property_id',
            'full_address',
            'city',
            'state',
            'zip',
            'county',
            'status',
            'auction_date',
            'opening_bid',
            'beds',
            'baths',
            'sqft',
            'year_built',
            'property_type',
            'owner_name',
            'owner_type',
            'assessed_value',
            'market_value',
            'location_quality',
            'location_confidence',
            'comps_median',
            'delta_percent',
            'hcad_url',
            'county_sale_url',
            'last_refreshed_at',
        ];

        const exportDate = new Date().toISOString();
        const rows = propertiesToExport.map(p => [
            sanitize(p.id),
            sanitize(p.address, 'TBD'),
            sanitize(p.city, 'TBD'),
            sanitize(p.state, 'TX'),
            sanitize(p.zip),
            sanitize(p.county, 'Harris'),
            sanitize(p.status, 'TBD'),
            sanitize(p.auctionDate, 'TBD'),
            sanitize(p.openingBid),
            sanitize(p.beds),
            sanitize(p.baths),
            sanitize(p.sqft),
            sanitize(p.yearBuilt),
            sanitize(p.propertyType, 'TBD'),
            sanitize(p.ownerName, 'TBD'),
            deriveOwnerType(p.ownerName),
            sanitize(p.assessedValue),
            sanitize(p.marketValue),
            getLocationQuality(p.locationConfidence, p.locationType),
            sanitize(p.locationConfidence, 'TBD'),
            sanitize(p.compsMedian),
            sanitize(p.deltaPercent),
            getHcadUrl(p.id),
            '', // county_sale_url - TBD when we have the source
            exportDate,
        ]);

        // Filename format: realsignal_harris_interested_YYYY-MM-DD.csv
        const dateStr = new Date().toISOString().split('T')[0];
        const county = propertiesToExport[0]?.county?.toLowerCase() || 'harris';

        exportToCSV({
            headers,
            rows,
            fileName: `realsignal_${county}_interested_${dateStr}`,
        });

        // Log export activity to backend for audit trail
        const userId = getUserId();
        if (userId) {
            fetch(
                `${import.meta.env.VITE_API_BASE || 'https://hivecore-backend-staging.up.railway.app'}/api/realsignal/activity/export`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('realsignal_access_token') || ''}`,
                    },
                    body: JSON.stringify({
                        exportType: 'csv',
                        count: propertiesToExport.length,
                        county,
                        filters: {
                            showInterestedOnly,
                            selectedCounty,
                            selectedPropertyTypes: Array.from(selectedPropertyTypes),
                            selectedStatuses: Array.from(selectedStatuses),
                        },
                    }),
                }
            ).catch(err => console.warn('Failed to log export activity:', err));
        }
    }, [
        showInterestedOnly,
        filteredProperties,
        interestedIds,
        getUserId,
        selectedCounty,
        selectedPropertyTypes,
        selectedStatuses,
    ]);

    const handleExportPDF = useCallback(() => {
        const propertiesToExport = showInterestedOnly
            ? filteredProperties
            : filteredProperties.filter(p => interestedIds.has(p.id));

        if (propertiesToExport.length === 0) {
            return;
        }

        const county = propertiesToExport[0]?.county?.toLowerCase() || 'harris';

        const pdfProperties: PropertyForPDF[] = propertiesToExport.map(p => ({
            id: p.id,
            address: p.address,
            city: p.city,
            state: p.state,
            zip: p.zip,
            county: p.county,
            status: p.status,
            auctionDate: p.auctionDate,
            openingBid: p.openingBid,
            beds: p.beds,
            baths: p.baths,
            sqft: p.sqft,
            yearBuilt: p.yearBuilt,
            propertyType: p.propertyType,
            ownerName: p.ownerName,
            assessedValue: p.assessedValue,
            marketValue: p.marketValue,
            locationConfidence: p.locationConfidence,
            locationType: p.locationType,
            compsMedian: p.compsMedian,
            deltaPercent: p.deltaPercent,
            lat: p.lat,
            lng: p.lng,
        }));

        exportToPDF({
            properties: pdfProperties,
            county,
        });

        const userId = getUserId();
        if (userId) {
            fetch(
                `${import.meta.env.VITE_API_BASE || 'https://hivecore-backend-staging.up.railway.app'}/api/realsignal/activity/export`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('realsignal_access_token') || ''}`,
                    },
                    body: JSON.stringify({
                        exportType: 'pdf',
                        count: propertiesToExport.length,
                        county,
                        filters: {
                            showInterestedOnly,
                            selectedCounty,
                            selectedPropertyTypes: Array.from(selectedPropertyTypes),
                            selectedStatuses: Array.from(selectedStatuses),
                        },
                    }),
                }
            ).catch(err => console.warn('Failed to log export activity:', err));
        }
    }, [
        showInterestedOnly,
        filteredProperties,
        interestedIds,
        getUserId,
        selectedCounty,
        selectedPropertyTypes,
        selectedStatuses,
    ]);

    // Export single property to PDF (for property detail drawer)
    const handleExportSingleProperty = useCallback(
        (property: Property) => {
            const county = property.county?.toLowerCase() || 'harris';

            const pdfProperty: PropertyForPDF = {
                id: property.id,
                address: property.address,
                city: property.city,
                state: property.state,
                zip: property.zip,
                county: property.county,
                status: property.status,
                auctionDate: property.auctionDate,
                openingBid: property.openingBid,
                beds: property.beds,
                baths: property.baths,
                sqft: property.sqft,
                yearBuilt: property.yearBuilt,
                propertyType: property.propertyType,
                ownerName: property.ownerName,
                assessedValue: property.assessedValue,
                marketValue: property.marketValue,
                locationConfidence: property.locationConfidence,
                locationType: property.locationType,
                compsMedian: property.compsMedian,
                deltaPercent: property.deltaPercent,
                lat: property.lat,
                lng: property.lng,
            };

            exportToPDF({
                properties: [pdfProperty],
                county,
            });

            const userId = getUserId();
            if (userId) {
                fetch(
                    `${import.meta.env.VITE_API_BASE || 'https://hivecore-backend-staging.up.railway.app'}/api/realsignal/activity/export`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${localStorage.getItem('realsignal_access_token') || ''}`,
                        },
                        body: JSON.stringify({
                            exportType: 'pdf_single',
                            count: 1,
                            county,
                            propertyId: property.id,
                        }),
                    }
                ).catch(err => console.warn('Failed to log export activity:', err));
            }
        },
        [getUserId]
    );

    // Export single property to CSV (for property detail drawer)
    const handleExportSinglePropertyCSV = useCallback(
        (property: Property) => {
            const county = property.county?.toLowerCase() || 'harris';

            // Helper to sanitize values
            const sanitize = (value: string | number | undefined | null, fallback = ''): string => {
                if (value === undefined || value === null) return fallback;
                if (typeof value === 'number') {
                    if (!Number.isFinite(value)) return fallback;
                    return value.toString();
                }
                const str = String(value).trim();
                if (str === '' || str === 'undefined' || str === 'null' || str === 'NaN') {
                    return fallback;
                }
                return str;
            };

            // Derive owner type from owner name
            const deriveOwnerType = (ownerName: string | undefined): string => {
                if (!ownerName) return 'TBD';
                const name = ownerName.toUpperCase();
                const entityIndicators = [
                    'LLC',
                    'INC',
                    'CORP',
                    'LP',
                    'LTD',
                    'TRUST',
                    'BANK',
                    'PROPERTIES',
                    'INVESTMENTS',
                    'HOLDINGS',
                    'PARTNERS',
                    'COMPANY',
                    'CO.',
                    'ASSOCIATION',
                    'FOUNDATION',
                ];
                for (const indicator of entityIndicators) {
                    if (name.includes(indicator)) return 'Entity';
                }
                return 'Individual';
            };

            // Map location confidence to quality label
            const getLocationQuality = (
                confidence: string | undefined,
                locationType: string | undefined
            ): string => {
                if (locationType === 'rooftop') return 'Rooftop';
                if (locationType === 'parcel_polygon') return 'Parcel';
                if (confidence === 'high') return 'High';
                if (confidence === 'low') return 'Approximate';
                return 'TBD';
            };

            // Generate HCAD URL from property ID
            const getHcadUrl = (propertyId: string): string => {
                const parts = propertyId.split(':');
                const accountNumber = parts[parts.length - 1];
                if (accountNumber && /^\d+$/.test(accountNumber)) {
                    return `https://hcad.org/property-search/real-property/real-property-search/?account=${accountNumber}`;
                }
                return '';
            };

            const headers = [
                'property_id',
                'full_address',
                'city',
                'state',
                'zip',
                'county',
                'status',
                'auction_date',
                'opening_bid',
                'beds',
                'baths',
                'sqft',
                'year_built',
                'property_type',
                'owner_name',
                'owner_type',
                'assessed_value',
                'market_value',
                'location_quality',
                'location_confidence',
                'comps_median',
                'delta_percent',
                'hcad_url',
                'county_sale_url',
                'last_refreshed_at',
            ];

            const exportDate = new Date().toISOString();
            const row = [
                sanitize(property.id),
                sanitize(property.address, 'TBD'),
                sanitize(property.city, 'TBD'),
                sanitize(property.state, 'TX'),
                sanitize(property.zip),
                sanitize(property.county, 'Harris'),
                sanitize(property.status, 'TBD'),
                sanitize(property.auctionDate, 'TBD'),
                sanitize(property.openingBid),
                sanitize(property.beds),
                sanitize(property.baths),
                sanitize(property.sqft),
                sanitize(property.yearBuilt),
                sanitize(property.propertyType, 'TBD'),
                sanitize(property.ownerName, 'TBD'),
                deriveOwnerType(property.ownerName),
                sanitize(property.assessedValue),
                sanitize(property.marketValue),
                getLocationQuality(property.locationConfidence, property.locationType),
                sanitize(property.locationConfidence, 'TBD'),
                sanitize(property.compsMedian),
                sanitize(property.deltaPercent),
                getHcadUrl(property.id),
                '',
                exportDate,
            ];

            const dateStr = new Date().toISOString().split('T')[0];
            const addressSlug =
                property.address
                    ?.replace(/[^a-zA-Z0-9]/g, '_')
                    .substring(0, 30)
                    .toLowerCase() || 'property';

            exportToCSV({
                headers,
                rows: [row],
                fileName: `realsignal_${county}_${addressSlug}_${dateStr}`,
            });

            const userId = getUserId();
            if (userId) {
                fetch(
                    `${import.meta.env.VITE_API_BASE || 'https://hivecore-backend-staging.up.railway.app'}/api/realsignal/activity/export`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${localStorage.getItem('realsignal_access_token') || ''}`,
                        },
                        body: JSON.stringify({
                            exportType: 'csv_single',
                            count: 1,
                            county,
                            propertyId: property.id,
                        }),
                    }
                ).catch(err => console.warn('Failed to log export activity:', err));
            }
        },
        [getUserId]
    );

    // Pipeline Board: Convert interested properties to pipeline format
    // Persist stages to localStorage (scoped by user) so they survive page refreshes
    const stageStorageKey = useMemo(() => {
        const uid = getUserId();
        return uid ? `realsignal_pipeline_stages_${uid}` : null;
    }, [getUserId]);

    const [propertyStages, setPropertyStages] = useState<Map<string, PipelineStage>>(() => {
        try {
            const uid = realSignalApi.getCurrentUserId();
            const key = uid ? `realsignal_pipeline_stages_${uid}` : null;
            if (key) {
                const saved = localStorage.getItem(key);
                if (saved) {
                    const entries: [string, PipelineStage][] = JSON.parse(saved);
                    return new Map(entries);
                }
            }
        } catch {
            /* ignore parse errors */
        }
        return new Map();
    });

    useEffect(() => {
        if (!stageStorageKey) return;
        try {
            const entries = Array.from(propertyStages.entries());
            localStorage.setItem(stageStorageKey, JSON.stringify(entries));
        } catch {
            /* ignore storage errors */
        }
    }, [propertyStages, stageStorageKey]);

    const pipelineProperties: PipelineProperty[] = useMemo(() => {
        const interestedProperties = properties.filter(p => interestedIds.has(p.id));
        return interestedProperties.map(p => {
            // Get canonical data from interested properties endpoint (Phase 5B.2D)
            const canonicalData = interestedPropertiesData.get(p.id);
            return {
                id: `pipeline-${p.id}`,
                propertyId: p.id,
                address: canonicalData?.summary?.display_address || p.address,
                city: p.city || '',
                status: p.status || '',
                auctionDate: p.auctionDate,
                openingBid: p.openingBid,
                stage: propertyStages.get(p.id) || 'interested',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                // Include canonical fields for pipeline board rendering
                canonical: canonicalData?.canonical,
                summary: canonicalData?.summary,
            };
        });
    }, [properties, interestedIds, propertyStages, interestedPropertiesData]);

    // Pipeline Board: Handle stage change with API call
    const handleStageChange = useCallback(
        async (propertyId: string, newStage: PipelineStage) => {
            const userId = getUserId();
            if (!userId) {
                console.warn('[Pipeline] Please sign in to change property stages');
                alert('Please sign in to change property stages');
                return;
            }

            // Optimistic update - persisted to localStorage via useEffect
            setPropertyStages(prev => {
                const next = new Map(prev);
                next.set(propertyId, newStage);
                return next;
            });

            // API call to persist stage change (best-effort)
            const result = await realSignalApi.updatePropertyStage(userId, propertyId, newStage);
            if (result.state !== 'success') {
                console.warn('[Pipeline] API sync failed, stage saved locally:', result.error);
            }
        },
        [getUserId]
    );

    return (
        <div
            style={{
                minHeight: '100vh',
                background: colors.background,
                fontFamily:
                    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                color: colors.textPrimary,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* CSS keyframes for pulsing animation */}
            <style>
                {`
                    @keyframes pulse-green {
                        0%, 100% {
                            opacity: 1;
                            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
                        }
                        50% {
                            opacity: 0.6;
                            box-shadow: 0 0 8px 2px rgba(16, 185, 129, 0.3);
                        }
                    }
                `}
            </style>
            {/* Top Navigation */}
            <nav
                style={{
                    height: '64px',
                    background: colors.backgroundLight,
                    borderBottom: `1px solid ${colors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 1rem',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}
            >
                <div className="flex items-center">
                    <MorphingLogo height="32px" />
                </div>

                <div style={{ flex: 1, maxWidth: '400px', margin: '0 1.5rem' }}>
                    <div className="relative">
                        <Search
                            style={{
                                position: 'absolute',
                                left: '0.75rem',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: colors.textMuted,
                                width: '1.25rem',
                                height: '1.25rem',
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Search by address, county, or document #..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            style={{
                                width: '100%',
                                background: 'rgba(0, 0, 0, 0.3)',
                                border: `1px solid rgba(255, 255, 255, 0.08)`,
                                borderRadius: '0.75rem',
                                padding: '0.5rem 1rem 0.5rem 2.5rem',
                                color: colors.textPrimary,
                                fontSize: '0.875rem',
                                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
                            }}
                        />
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    {/* View Toggle: Map / Pipeline */}
                    <div
                        style={{
                            display: 'flex',
                            background: colors.background,
                            borderRadius: '0.5rem',
                            padding: '2px',
                            border: `1px solid ${colors.border}`,
                        }}
                    >
                        <button
                            onClick={() => setViewMode('map')}
                            style={{
                                padding: '0.375rem 0.75rem',
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                background: viewMode === 'map' ? colors.accent : 'transparent',
                                color: viewMode === 'map' ? 'white' : colors.textSecondary,
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                            }}
                        >
                            <MapPin className="w-3.5 h-3.5 inline mr-1" />
                            Map
                        </button>
                        <button
                            onClick={() => setViewMode('pipeline')}
                            style={{
                                padding: '0.375rem 0.75rem',
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                background: viewMode === 'pipeline' ? colors.accent : 'transparent',
                                color: viewMode === 'pipeline' ? 'white' : colors.textSecondary,
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                            }}
                        >
                            Pipeline
                            {interestedIds.size > 0 && (
                                <span
                                    style={{
                                        marginLeft: '0.375rem',
                                        background:
                                            viewMode === 'pipeline'
                                                ? 'rgba(255,255,255,0.2)'
                                                : colors.backgroundCard,
                                        padding: '0.125rem 0.375rem',
                                        borderRadius: '0.25rem',
                                        fontSize: '0.625rem',
                                    }}
                                >
                                    {interestedIds.size}
                                </span>
                            )}
                        </button>
                    </div>

                    <button
                        className="relative p-2"
                        style={{
                            color: colors.textSecondary,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                        onClick={onNavigateToAlerts}
                    >
                        <Bell className="w-5 h-5" />
                        <span
                            style={{
                                position: 'absolute',
                                top: '-4px',
                                right: '-4px',
                                width: '18px',
                                height: '18px',
                                background: colors.accent,
                                color: 'white',
                                fontSize: '0.625rem',
                                fontWeight: 700,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            3
                        </span>
                    </button>
                    <button
                        className="flex items-center gap-2 p-2"
                        style={{
                            color: colors.textSecondary,
                            cursor: 'pointer',
                            background: 'transparent',
                            border: 'none',
                        }}
                        onClick={() => setShowSettings(true)}
                        title="Settings"
                    >
                        <div
                            style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                background: colors.backgroundCard,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <User className="w-4 h-4" />
                        </div>
                        <ChevronDown className="w-4 h-4" />
                    </button>
                </div>
            </nav>

            {/* API Status Banner */}
            {apiState === 'notDeployed' && (
                <div
                    style={{
                        background: colors.backgroundCard,
                        borderBottom: `1px solid ${colors.border}`,
                        padding: '0.75rem 1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <ServerOff
                            style={{ width: '1.25rem', height: '1.25rem', color: colors.warning }}
                        />
                        <span style={{ fontSize: '0.875rem', color: colors.textSecondary }}>
                            RealSignal service not deployed in this environment. Showing demo data.
                        </span>
                    </div>
                    <button
                        onClick={handleCheckAgain}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.375rem 0.75rem',
                            background: colors.accent,
                            border: 'none',
                            borderRadius: '0.375rem',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                        }}
                    >
                        <RefreshCw style={{ width: '0.875rem', height: '0.875rem' }} />
                        Check again
                    </button>
                </div>
            )}

            {apiState === 'authError' && (
                <div
                    style={{
                        background: colors.backgroundCard,
                        borderBottom: `1px solid ${colors.border}`,
                        padding: '0.75rem 1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                    }}
                >
                    <AlertCircle
                        style={{ width: '1.25rem', height: '1.25rem', color: colors.error }}
                    />
                    <span style={{ fontSize: '0.875rem', color: colors.textSecondary }}>
                        Not authorized to access RealSignal data. Please sign in.
                    </span>
                </div>
            )}

            {apiState === 'error' && (
                <div
                    style={{
                        background: colors.backgroundCard,
                        borderBottom: `1px solid ${colors.border}`,
                        padding: '0.75rem 1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <AlertCircle
                            style={{ width: '1.25rem', height: '1.25rem', color: colors.error }}
                        />
                        <span style={{ fontSize: '0.875rem', color: colors.textSecondary }}>
                            {apiError || 'Failed to load data. Showing demo data.'}
                        </span>
                    </div>
                    <button
                        onClick={handleCheckAgain}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.375rem 0.75rem',
                            background: colors.accent,
                            border: 'none',
                            borderRadius: '0.375rem',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                        }}
                    >
                        <RefreshCw style={{ width: '0.875rem', height: '0.875rem' }} />
                        Retry
                    </button>
                </div>
            )}

            {apiState === 'empty' && (
                <div
                    style={{
                        background: colors.backgroundCard,
                        borderBottom: `1px solid ${colors.border}`,
                        padding: '0.75rem 1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                    }}
                >
                    <MapPin
                        style={{ width: '1.25rem', height: '1.25rem', color: colors.textMuted }}
                    />
                    <span style={{ fontSize: '0.875rem', color: colors.textSecondary }}>
                        No properties found in the current view. Try adjusting your filters or
                        search area.
                    </span>
                </div>
            )}

            {/* Main Content */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                {/* Filters Panel */}
                {showFilters && (
                    <aside
                        style={{
                            width: '300px',
                            background: colors.backgroundLight,
                            borderRight: `1px solid rgba(255, 255, 255, 0.06)`,
                            overflowY: 'auto',
                            padding: '1.25rem 1.5rem',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '4px 0 16px rgba(0, 0, 0, 0.2)',
                        }}
                    >
                        {/* Include Approximate Toggle */}
                        <div
                            style={{
                                marginBottom: '1.5rem',
                                padding: '0.75rem',
                                background: colors.backgroundWell,
                                borderRadius: '0.5rem',
                                border: `1px solid ${colors.border}`,
                            }}
                        >
                            <label className="flex items-center justify-between cursor-pointer">
                                <span style={{ fontSize: '0.875rem', color: colors.textSecondary }}>
                                    Include approximate locations
                                </span>
                                <button
                                    onClick={() => setIncludeApproximate(!includeApproximate)}
                                    style={{
                                        position: 'relative',
                                        width: '44px',
                                        height: '24px',
                                        background: includeApproximate
                                            ? colors.accent
                                            : colors.background,
                                        border: `1px solid ${includeApproximate ? colors.accent : colors.border}`,
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <span
                                        style={{
                                            position: 'absolute',
                                            top: '2px',
                                            left: includeApproximate ? '22px' : '2px',
                                            width: '18px',
                                            height: '18px',
                                            background: 'white',
                                            borderRadius: '50%',
                                            transition: 'left 150ms ease',
                                        }}
                                    />
                                </button>
                            </label>
                        </div>

                        {/* Filter Sections */}
                        {/* Location Selector - Home Base Model (single county selection) */}
                        <FilterSection title="Location">
                            <div
                                style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}
                            >
                                {(Object.keys(COUNTY_CONFIG) as CountyId[]).map(countyId => {
                                    const config = COUNTY_CONFIG[countyId];
                                    const isSelected = selectedCounty === countyId;
                                    const isDisabled = !config.enabled;

                                    return (
                                        <div
                                            key={countyId}
                                            title={
                                                isDisabled
                                                    ? 'Coming soon — Data not yet available for this county'
                                                    : undefined
                                            }
                                            onClick={() => handleCountySelect(countyId)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                padding: '0.375rem 0.5rem',
                                                marginLeft: '-0.5rem',
                                                marginRight: '-0.5rem',
                                                borderRadius: '0.25rem',
                                                transition: 'background 150ms ease',
                                                opacity: isDisabled ? 0.5 : 1,
                                                background: isSelected
                                                    ? 'rgba(58, 106, 122, 0.2)'
                                                    : 'transparent',
                                            }}
                                            onMouseEnter={e => {
                                                if (!isDisabled) {
                                                    e.currentTarget.style.background = isSelected
                                                        ? 'rgba(58, 106, 122, 0.3)'
                                                        : 'rgba(255, 255, 255, 0.05)';
                                                }
                                            }}
                                            onMouseLeave={e => {
                                                e.currentTarget.style.background = isSelected
                                                    ? 'rgba(58, 106, 122, 0.2)'
                                                    : 'transparent';
                                            }}
                                        >
                                            {/* Radio-style indicator for single selection */}
                                            <div
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    borderRadius: '50%',
                                                    border: `2px solid ${isSelected ? colors.accent : colors.border}`,
                                                    background: colors.background,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {isSelected && (
                                                    <div
                                                        style={{
                                                            width: '8px',
                                                            height: '8px',
                                                            borderRadius: '50%',
                                                            background: colors.accent,
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <span
                                                style={{
                                                    marginLeft: '0.625rem',
                                                    fontSize: '0.875rem',
                                                    color: isDisabled
                                                        ? colors.textMuted
                                                        : colors.textPrimary,
                                                }}
                                            >
                                                {config.name}
                                            </span>
                                            {isDisabled && (
                                                <span
                                                    style={{
                                                        marginLeft: 'auto',
                                                        fontSize: '0.625rem',
                                                        color: colors.textMuted,
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    Coming soon
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </FilterSection>

                        <FilterSection
                            title={
                                hasActiveBedsOrBathsFilters ? (
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                        }}
                                    >
                                        Property
                                        <span
                                            style={{
                                                background: colors.accent,
                                                color: 'white',
                                                fontSize: '0.625rem',
                                                fontWeight: 600,
                                                padding: '0.125rem 0.375rem',
                                                borderRadius: '0.25rem',
                                            }}
                                        >
                                            Active
                                        </span>
                                    </span>
                                ) : (
                                    'Property'
                                )
                            }
                        >
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.75rem',
                                            color: colors.textSecondary,
                                            marginBottom: '0.5rem',
                                        }}
                                    >
                                        Beds (min)
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="Any"
                                        min={0}
                                        value={bedsMin ?? ''}
                                        onChange={e =>
                                            setBedsMin(
                                                e.target.value === ''
                                                    ? undefined
                                                    : Number(e.target.value)
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            background: colors.background,
                                            border: `1px solid ${bedsMin !== undefined ? colors.accent : colors.border}`,
                                            borderRadius: '0.5rem',
                                            padding: '0.5rem',
                                            color: colors.textPrimary,
                                            fontSize: '0.875rem',
                                        }}
                                    />
                                </div>
                                <div>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.75rem',
                                            color: colors.textSecondary,
                                            marginBottom: '0.5rem',
                                        }}
                                    >
                                        Beds (max)
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="Any"
                                        min={0}
                                        value={bedsMax ?? ''}
                                        onChange={e =>
                                            setBedsMax(
                                                e.target.value === ''
                                                    ? undefined
                                                    : Number(e.target.value)
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            background: colors.background,
                                            border: `1px solid ${bedsMax !== undefined ? colors.accent : colors.border}`,
                                            borderRadius: '0.5rem',
                                            padding: '0.5rem',
                                            color: colors.textPrimary,
                                            fontSize: '0.875rem',
                                        }}
                                    />
                                </div>
                            </div>
                            <div
                                className="grid grid-cols-2 gap-3"
                                style={{ marginTop: '0.75rem' }}
                            >
                                <div>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.75rem',
                                            color: colors.textSecondary,
                                            marginBottom: '0.5rem',
                                        }}
                                    >
                                        Baths (min)
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="Any"
                                        min={0}
                                        step={0.5}
                                        value={bathsMin ?? ''}
                                        onChange={e =>
                                            setBathsMin(
                                                e.target.value === ''
                                                    ? undefined
                                                    : Number(e.target.value)
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            background: colors.background,
                                            border: `1px solid ${bathsMin !== undefined ? colors.accent : colors.border}`,
                                            borderRadius: '0.5rem',
                                            padding: '0.5rem',
                                            color: colors.textPrimary,
                                            fontSize: '0.875rem',
                                        }}
                                    />
                                </div>
                                <div>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.75rem',
                                            color: colors.textSecondary,
                                            marginBottom: '0.5rem',
                                        }}
                                    >
                                        Baths (max)
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="Any"
                                        min={0}
                                        step={0.5}
                                        value={bathsMax ?? ''}
                                        onChange={e =>
                                            setBathsMax(
                                                e.target.value === ''
                                                    ? undefined
                                                    : Number(e.target.value)
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            background: colors.background,
                                            border: `1px solid ${bathsMax !== undefined ? colors.accent : colors.border}`,
                                            borderRadius: '0.5rem',
                                            padding: '0.5rem',
                                            color: colors.textPrimary,
                                            fontSize: '0.875rem',
                                        }}
                                    />
                                </div>
                            </div>
                            {/* Sq Ft Filter - V1 Core physical viability */}
                            <div
                                className="grid grid-cols-2 gap-3"
                                style={{ marginTop: '0.75rem' }}
                            >
                                <div>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.75rem',
                                            color: colors.textSecondary,
                                            marginBottom: '0.5rem',
                                        }}
                                    >
                                        Sq Ft (min)
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="Any"
                                        min={0}
                                        step={100}
                                        value={sqftMin ?? ''}
                                        onChange={e =>
                                            setSqftMin(
                                                e.target.value === ''
                                                    ? undefined
                                                    : Number(e.target.value)
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            background: colors.background,
                                            border: `1px solid ${sqftMin !== undefined ? colors.accent : colors.border}`,
                                            borderRadius: '0.5rem',
                                            padding: '0.5rem',
                                            color: colors.textPrimary,
                                            fontSize: '0.875rem',
                                        }}
                                    />
                                </div>
                                <div>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.75rem',
                                            color: colors.textSecondary,
                                            marginBottom: '0.5rem',
                                        }}
                                    >
                                        Sq Ft (max)
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="Any"
                                        min={0}
                                        step={100}
                                        value={sqftMax ?? ''}
                                        onChange={e =>
                                            setSqftMax(
                                                e.target.value === ''
                                                    ? undefined
                                                    : Number(e.target.value)
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            background: colors.background,
                                            border: `1px solid ${sqftMax !== undefined ? colors.accent : colors.border}`,
                                            borderRadius: '0.5rem',
                                            padding: '0.5rem',
                                            color: colors.textPrimary,
                                            fontSize: '0.875rem',
                                        }}
                                    />
                                </div>
                            </div>
                        </FilterSection>

                        {/* Property Type Filter - V1 Core physical viability */}
                        <FilterSection
                            title={
                                <span
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                    }}
                                >
                                    Property Type
                                    {selectedPropertyTypes.size > 0 &&
                                        selectedPropertyTypes.size < ALL_PROPERTY_TYPES.length && (
                                            <span
                                                style={{
                                                    background: colors.accent,
                                                    color: colors.textPrimary,
                                                    fontSize: '0.5625rem',
                                                    fontWeight: 600,
                                                    padding: '0.125rem 0.375rem',
                                                    borderRadius: '0.25rem',
                                                }}
                                            >
                                                {selectedPropertyTypes.size} selected
                                            </span>
                                        )}
                                </span>
                            }
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0.25rem',
                                }}
                            >
                                {ALL_PROPERTY_TYPES.map(propType => (
                                    <label
                                        key={propType}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            padding: '0.375rem 0.5rem',
                                            marginLeft: '-0.5rem',
                                            marginRight: '-0.5rem',
                                            borderRadius: '0.25rem',
                                            background: selectedPropertyTypes.has(propType)
                                                ? colors.backgroundWell
                                                : 'transparent',
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedPropertyTypes.has(propType)}
                                            onChange={() => {
                                                setSelectedPropertyTypes(prev => {
                                                    const next = new Set(prev);
                                                    if (next.has(propType)) {
                                                        next.delete(propType);
                                                    } else {
                                                        next.add(propType);
                                                    }
                                                    return next;
                                                });
                                            }}
                                            style={{
                                                borderRadius: '0.25rem',
                                                borderColor: selectedPropertyTypes.has(propType)
                                                    ? colors.accent
                                                    : colors.border,
                                                background: colors.background,
                                                marginRight: '0.5rem',
                                            }}
                                        />
                                        <span
                                            style={{
                                                fontSize: '0.8125rem',
                                                color: selectedPropertyTypes.has(propType)
                                                    ? colors.textPrimary
                                                    : colors.textSecondary,
                                            }}
                                        >
                                            {propType}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </FilterSection>

                        {/* Foreclosure Status Filter */}
                        <FilterSection
                            title={
                                <span
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                    }}
                                >
                                    Foreclosure Status
                                    {hasActiveStatusFilter && (
                                        <span
                                            style={{
                                                background: colors.accent,
                                                color: colors.textPrimary,
                                                fontSize: '0.5625rem',
                                                fontWeight: 600,
                                                padding: '0.125rem 0.375rem',
                                                borderRadius: '0.25rem',
                                            }}
                                        >
                                            {selectedStatuses.size} selected
                                        </span>
                                    )}
                                </span>
                            }
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0.25rem',
                                }}
                            >
                                {ALL_STATUSES.map(status => (
                                    <label
                                        key={status}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            padding: '0.375rem 0.5rem',
                                            marginLeft: '-0.5rem',
                                            marginRight: '-0.5rem',
                                            borderRadius: '0.25rem',
                                            background: selectedStatuses.has(status)
                                                ? colors.backgroundWell
                                                : 'transparent',
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedStatuses.has(status)}
                                            onChange={() => {
                                                setSelectedStatuses(prev => {
                                                    const next = new Set(prev);
                                                    if (next.has(status)) {
                                                        next.delete(status);
                                                    } else {
                                                        next.add(status);
                                                    }
                                                    return next;
                                                });
                                            }}
                                            style={{
                                                borderRadius: '0.25rem',
                                                borderColor: selectedStatuses.has(status)
                                                    ? colors.accent
                                                    : colors.border,
                                                background: colors.background,
                                            }}
                                        />
                                        <span
                                            style={{
                                                marginLeft: '0.625rem',
                                                fontSize: '0.875rem',
                                                color: selectedStatuses.has(status)
                                                    ? colors.textPrimary
                                                    : colors.textSecondary,
                                            }}
                                        >
                                            {status}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </FilterSection>

                        {/* Deal Criteria Filter */}
                        <FilterSection
                            title={
                                <span
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                    }}
                                >
                                    Deal Criteria
                                    {hasAppliedCriteria && (
                                        <span
                                            style={{
                                                background: colors.accent,
                                                color: colors.textPrimary,
                                                fontSize: '0.5625rem',
                                                fontWeight: 600,
                                                padding: '0.125rem 0.375rem',
                                                borderRadius: '0.25rem',
                                            }}
                                        >
                                            Active
                                        </span>
                                    )}
                                </span>
                            }
                            defaultOpen={false}
                            isAdvanced
                        >
                            {/* Max Opening Bid */}
                            <div style={{ marginBottom: '0.75rem' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.75rem',
                                        color: colors.textSecondary,
                                        marginBottom: '0.5rem',
                                    }}
                                >
                                    Max Opening Bid
                                </label>
                                <input
                                    type="number"
                                    placeholder="Any amount"
                                    value={draftCriteria.maxOpeningBid ?? ''}
                                    onChange={e =>
                                        setDraftCriteria(prev => ({
                                            ...prev,
                                            maxOpeningBid: e.target.value
                                                ? Number(e.target.value)
                                                : undefined,
                                        }))
                                    }
                                    onKeyDown={handleCriteriaKeyDown}
                                    style={{
                                        width: '100%',
                                        background: colors.background,
                                        border: `1px solid ${appliedCriteria.maxOpeningBid !== undefined ? colors.accent : colors.border}`,
                                        borderRadius: '0.5rem',
                                        padding: '0.5rem',
                                        color: colors.textPrimary,
                                        fontSize: '0.875rem',
                                    }}
                                />
                            </div>

                            {/* Min Opening Bid */}
                            <div style={{ marginBottom: '0.75rem' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.75rem',
                                        color: colors.textSecondary,
                                        marginBottom: '0.5rem',
                                    }}
                                >
                                    Min Opening Bid
                                </label>
                                <input
                                    type="number"
                                    placeholder="Any amount"
                                    value={draftCriteria.minOpeningBid ?? ''}
                                    onChange={e =>
                                        setDraftCriteria(prev => ({
                                            ...prev,
                                            minOpeningBid: e.target.value
                                                ? Number(e.target.value)
                                                : undefined,
                                        }))
                                    }
                                    onKeyDown={handleCriteriaKeyDown}
                                    style={{
                                        width: '100%',
                                        background: colors.background,
                                        border: `1px solid ${appliedCriteria.minOpeningBid !== undefined ? colors.accent : colors.border}`,
                                        borderRadius: '0.5rem',
                                        padding: '0.5rem',
                                        color: colors.textPrimary,
                                        fontSize: '0.875rem',
                                    }}
                                />
                            </div>

                            {/* Auction Within Days */}
                            <div style={{ marginBottom: '0.75rem' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.75rem',
                                        color: colors.textSecondary,
                                        marginBottom: '0.5rem',
                                    }}
                                >
                                    Auction Within
                                </label>
                                <select
                                    value={draftCriteria.auctionWithinDays ?? ''}
                                    onChange={e => {
                                        const value = e.target.value
                                            ? Number(e.target.value)
                                            : undefined;
                                        setDraftCriteria(prev => ({
                                            ...prev,
                                            auctionWithinDays: value,
                                        }));
                                        // Auto-apply for dropdown since it's a discrete selection
                                        setAppliedCriteria(prev => ({
                                            ...prev,
                                            auctionWithinDays: value,
                                        }));
                                    }}
                                    style={{
                                        width: '100%',
                                        background: colors.background,
                                        border: `1px solid ${appliedCriteria.auctionWithinDays !== undefined ? colors.accent : colors.border}`,
                                        borderRadius: '0.5rem',
                                        padding: '0.5rem',
                                        color: colors.textPrimary,
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    <option value="">Any time</option>
                                    <option value="7">Within 7 days</option>
                                    <option value="14">Within 14 days</option>
                                    <option value="21">Within 21 days</option>
                                    <option value="30">Within 30 days</option>
                                    <option value="60">Within 60 days</option>
                                    <option value="90">Within 90 days</option>
                                </select>
                            </div>

                            {/* Has Comps */}
                            <div style={{ marginBottom: '0.5rem' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.75rem',
                                        color: colors.textSecondary,
                                        marginBottom: '0.5rem',
                                    }}
                                >
                                    Comparable Data
                                </label>
                                <div style={{ display: 'flex', gap: '0.75rem' }}>
                                    <label
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            name="hasComps"
                                            checked={draftCriteria.hasComps === undefined}
                                            onChange={() => {
                                                setDraftCriteria(prev => ({
                                                    ...prev,
                                                    hasComps: undefined,
                                                }));
                                                // Auto-apply for radio since it's a discrete selection
                                                setAppliedCriteria(prev => ({
                                                    ...prev,
                                                    hasComps: undefined,
                                                }));
                                            }}
                                            style={{ marginRight: '0.375rem' }}
                                        />
                                        <span
                                            style={{
                                                fontSize: '0.8125rem',
                                                color: colors.textPrimary,
                                            }}
                                        >
                                            Any
                                        </span>
                                    </label>
                                    <label
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            name="hasComps"
                                            checked={draftCriteria.hasComps === true}
                                            onChange={() => {
                                                setDraftCriteria(prev => ({
                                                    ...prev,
                                                    hasComps: true,
                                                }));
                                                // Auto-apply for radio since it's a discrete selection
                                                setAppliedCriteria(prev => ({
                                                    ...prev,
                                                    hasComps: true,
                                                }));
                                            }}
                                            style={{ marginRight: '0.375rem' }}
                                        />
                                        <span
                                            style={{
                                                fontSize: '0.8125rem',
                                                color: colors.textPrimary,
                                            }}
                                        >
                                            Has comps
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </FilterSection>

                        <div className="mt-6 space-y-3">
                            <button
                                onClick={handleApplyCriteria}
                                style={{
                                    width: '100%',
                                    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
                                    color: 'white',
                                    fontWeight: 600,
                                    padding: '0.75rem',
                                    borderRadius: '0.75rem',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                Apply Filters
                            </button>
                            <button
                                onClick={() => {
                                    // V1 UI Fix #4: Reset clears all filters and recenters to Harris
                                    setDraftCriteria({});
                                    setAppliedCriteria({});
                                    setIncludeApproximate(false);
                                    setBedsMin(undefined);
                                    setBedsMax(undefined);
                                    setBathsMin(undefined);
                                    setBathsMax(undefined);
                                    setSelectedStatuses(new Set());
                                    // Recenter map to Harris County baseline
                                    centerMapToCounty('harris');
                                }}
                                style={{
                                    width: '100%',
                                    background: 'transparent',
                                    color: colors.textSecondary,
                                    padding: '0.5rem',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                Reset Filters
                            </button>
                        </div>

                        {/* Company Logo */}
                        <div
                            style={{
                                marginTop: 'auto',
                                paddingTop: '1.5rem',
                                display: 'flex',
                                justifyContent: 'flex-start',
                            }}
                        >
                            <img
                                src="/assets/modular-grid-logo.png"
                                alt="Modular System"
                                style={{
                                    height: '32px',
                                    width: 'auto',
                                    opacity: 0.6,
                                }}
                            />
                        </div>
                    </aside>
                )}

                {/* Main Content Area - Map or Pipeline Board */}
                {viewMode === 'pipeline' ? (
                    <div style={{ flex: 1, overflow: 'auto', background: colors.background }}>
                        <PipelineBoard
                            properties={pipelineProperties}
                            onStageChange={handleStageChange}
                            onClose={() => setViewMode('map')}
                            colors={{
                                background: colors.background,
                                backgroundLight: colors.backgroundLight,
                                textPrimary: colors.textPrimary,
                                textSecondary: colors.textSecondary,
                                textMuted: colors.textMuted,
                                border: colors.border,
                                accent: colors.accent,
                            }}
                        />
                    </div>
                ) : (
                    <>
                        {/* Map Section with bottom padding bar */}
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {/* Map Container with instrument bezel */}
                            <div
                                style={{
                                    flex: 1,
                                    position: 'relative',
                                    background: '#0B1C2D',
                                    padding: '2px',
                                }}
                            >
                                {/* Inner bezel edge */}
                                <div
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        position: 'relative',
                                        background: colors.backgroundWell,
                                        borderRadius: '2px',
                                        boxShadow:
                                            'inset 0 0 0 1px #132A3D, inset 0 2px 8px rgba(0, 0, 0, 0.4)',
                                    }}
                                >
                                    {isLoaded ? (
                                        <GoogleMap
                                            mapContainerStyle={{
                                                ...mapContainerStyle,
                                                height: 'calc(100% - 36px)',
                                            }}
                                            center={mapCenter || initialCenter}
                                            zoom={mapZoom || COUNTY_CONFIG[selectedCounty].zoom}
                                            onLoad={onLoad}
                                            onUnmount={onUnmount}
                                            onIdle={handleMapIdle}
                                            onZoomChanged={() => {
                                                if (mapRef.current) {
                                                    setCurrentZoom(
                                                        mapRef.current.getZoom() ||
                                                            COUNTY_CONFIG[selectedCounty].zoom
                                                    );
                                                }
                                            }}
                                            options={{
                                                styles: mapStyles,
                                                mapTypeId: 'roadmap',
                                                disableDefaultUI: true,
                                                zoomControl: false,
                                                mapTypeControl: false,
                                                streetViewControl: false,
                                                fullscreenControl: false,
                                            }}
                                        >
                                            {filteredProperties.map(property => {
                                                const isSelected =
                                                    property.id === selectedPropertyId;
                                                const isFlashing = property.id === flashingPinId;
                                                const isHighConfidence =
                                                    property.locationConfidence === 'high';
                                                const baseScale = currentZoom > 10 ? 1.7 : 2.0;
                                                const pinScale = isSelected
                                                    ? baseScale * 1.2
                                                    : baseScale;

                                                // Determine pin color - flash white when flashing, otherwise normal colors
                                                const normalColor = isSelected
                                                    ? colors.accent
                                                    : isHighConfidence
                                                      ? colors.success
                                                      : colors.warning;

                                                const pinSize = isFlashing
                                                    ? 48
                                                    : isSelected
                                                      ? 40
                                                      : 32;
                                                const pinColor = isFlashing
                                                    ? '#ffffff'
                                                    : normalColor;
                                                return (
                                                    <Marker
                                                        key={property.id}
                                                        position={{
                                                            lat: property.lat,
                                                            lng: property.lng,
                                                        }}
                                                        onClick={() => handlePinClick(property)}
                                                        icon={{
                                                            url: createColoredPinSvg(pinColor),
                                                            scaledSize: new google.maps.Size(
                                                                pinSize,
                                                                pinSize
                                                            ),
                                                            anchor: new google.maps.Point(
                                                                pinSize / 2,
                                                                pinSize
                                                            ),
                                                        }}
                                                        zIndex={
                                                            isSelected || isFlashing
                                                                ? 1000
                                                                : isHighConfidence
                                                                  ? 100
                                                                  : 50
                                                        }
                                                        animation={
                                                            isFlashing
                                                                ? google.maps.Animation.BOUNCE
                                                                : undefined
                                                        }
                                                    />
                                                );
                                            })}

                                            {/* Searched location marker */}
                                            {searchedLocation && (
                                                <Marker
                                                    position={{
                                                        lat: searchedLocation.lat,
                                                        lng: searchedLocation.lng,
                                                    }}
                                                    icon={{
                                                        url: createColoredPinSvg(colors.accent),
                                                        scaledSize: new google.maps.Size(48, 48),
                                                        anchor: new google.maps.Point(24, 48),
                                                    }}
                                                    zIndex={2000}
                                                />
                                            )}
                                        </GoogleMap>
                                    ) : (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: colors.textMuted,
                                            }}
                                        >
                                            Loading map...
                                        </div>
                                    )}

                                    {/* Map controls */}
                                    <div className="absolute top-4 right-4 flex flex-col gap-2">
                                        <button
                                            onClick={handleZoomIn}
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                background: colors.backgroundLight,
                                                border: `1px solid rgba(255, 255, 255, 0.08)`,
                                                borderRadius: '0.5rem',
                                                color: colors.textSecondary,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '1.25rem',
                                                fontWeight: 700,
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                            }}
                                        >
                                            +
                                        </button>
                                        <button
                                            onClick={handleZoomOut}
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                background: colors.backgroundLight,
                                                border: `1px solid rgba(255, 255, 255, 0.08)`,
                                                borderRadius: '0.5rem',
                                                color: colors.textSecondary,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '1.25rem',
                                                fontWeight: 700,
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                            }}
                                        >
                                            -
                                        </button>
                                    </div>

                                    {/* Legend - Only show Approx indicator when includeApproximate is enabled */}
                                    {includeApproximate && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                bottom: '2.5rem',
                                                left: '1rem',
                                                background: `${colors.backgroundLight}E6`,
                                                border: `1px solid rgba(255, 255, 255, 0.08)`,
                                                borderRadius: '0.375rem',
                                                padding: '0.5rem 0.625rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.75rem',
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                            }}
                                        >
                                            <div className="flex items-center gap-1">
                                                <div
                                                    style={{
                                                        width: '8px',
                                                        height: '8px',
                                                        borderRadius: '50%',
                                                        background: colors.warning,
                                                    }}
                                                />
                                                <span
                                                    style={{
                                                        fontSize: '0.625rem',
                                                        color: colors.textMuted,
                                                    }}
                                                >
                                                    Approx
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Property count */}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '1rem',
                                            left: '1rem',
                                            background: `${colors.backgroundLight}E6`,
                                            border: `1px solid rgba(255, 255, 255, 0.08)`,
                                            borderRadius: '0.375rem',
                                            padding: '0.375rem 0.625rem',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                        }}
                                    >
                                        <span
                                            style={{ fontSize: '0.75rem', color: colors.textMuted }}
                                        >
                                            {filteredProperties.length}
                                        </span>
                                    </div>

                                    {/* Toggle filters button */}
                                    <button
                                        onClick={() => setShowFilters(!showFilters)}
                                        style={{
                                            position: 'absolute',
                                            top: '1rem',
                                            left: showFilters ? '296px' : '1rem',
                                            background: colors.backgroundLight,
                                            border: `1px solid ${colors.border}`,
                                            borderRadius: '0.5rem',
                                            padding: '0.5rem',
                                            color: colors.textSecondary,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            transition: 'left 200ms ease',
                                        }}
                                    >
                                        <Filter className="w-4 h-4" />
                                        {!showFilters && (
                                            <span style={{ fontSize: '0.875rem' }}>Filters</span>
                                        )}
                                    </button>

                                    {/* Vignette overlay for depth */}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            pointerEvents: 'none',
                                            background:
                                                'radial-gradient(ellipse at center, transparent 40%, rgba(0, 0, 0, 0.15) 100%)',
                                            borderRadius: '2px',
                                        }}
                                    />

                                    {/* Glass overlay for premium feel */}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            pointerEvents: 'none',
                                            background:
                                                'linear-gradient(180deg, rgba(255, 255, 255, 0.02) 0%, transparent 20%)',
                                            borderRadius: '2px',
                                        }}
                                    />

                                    {/* Bottom Event Ticker - Market event tape */}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            height: '36px',
                                            background: '#0A1520',
                                            borderTop: '1px solid #132A3D',
                                            boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            paddingLeft: '1rem',
                                            paddingRight: '1rem',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '2rem',
                                                fontSize: '12px',
                                                color: colors.textMuted,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <span>
                                                <span
                                                    style={{
                                                        color: colors.success,
                                                        fontWeight: 500,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em',
                                                    }}
                                                >
                                                    {COUNTY_CONFIG[selectedCounty].name}
                                                </span>
                                                <span style={{ margin: '0 0.5rem', opacity: 0.4 }}>
                                                    ·
                                                </span>
                                                <span>{filteredProperties.length} properties</span>
                                            </span>
                                            {interestedIds.size > 0 && (
                                                <span>
                                                    <span
                                                        style={{
                                                            color: colors.accent,
                                                            fontWeight: 500,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em',
                                                        }}
                                                    >
                                                        WATCHLIST
                                                    </span>
                                                    <span
                                                        style={{ margin: '0 0.5rem', opacity: 0.4 }}
                                                    >
                                                        ·
                                                    </span>
                                                    <span>{interestedIds.size} starred</span>
                                                </span>
                                            )}
                                            {hasAppliedCriteria && (
                                                <span>
                                                    <span
                                                        style={{
                                                            color: colors.warning,
                                                            fontWeight: 500,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em',
                                                        }}
                                                    >
                                                        FILTERS
                                                    </span>
                                                    <span
                                                        style={{ margin: '0 0.5rem', opacity: 0.4 }}
                                                    >
                                                        ·
                                                    </span>
                                                    <span>Active</span>
                                                </span>
                                            )}
                                        </div>
                                        {/* Verified indicator with pulsing animation */}
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                fontSize: '12px',
                                                color: colors.textMuted,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '50%',
                                                    background: colors.success,
                                                    animation:
                                                        'pulse-green 2s ease-in-out infinite',
                                                }}
                                            />
                                            <span
                                                style={{
                                                    fontWeight: 500,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                }}
                                            >
                                                Verified
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Bottom Padding Bar - matches pagination section styling */}
                            <div
                                style={{
                                    padding: '0.75rem 1rem',
                                    borderTop: `1px solid ${colors.border}`,
                                    background: colors.backgroundCard,
                                }}
                            />
                        </div>

                        {/* Results List */}
                        <aside
                            style={{
                                width: '360px',
                                background: colors.background,
                                borderLeft: `1px solid rgba(255, 255, 255, 0.06)`,
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.2)',
                            }}
                        >
                            {/* Results Header */}
                            <div
                                style={{
                                    padding: '1rem',
                                    borderBottom: `1px solid ${colors.border}`,
                                }}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <h2
                                        style={{
                                            fontSize: '1.125rem',
                                            fontWeight: 600,
                                            color: colors.textPrimary,
                                        }}
                                    >
                                        Results
                                        <span
                                            style={{
                                                marginLeft: '0.5rem',
                                                fontSize: '0.875rem',
                                                fontWeight: 400,
                                                color: colors.textMuted,
                                            }}
                                        >
                                            ({filteredProperties.length})
                                        </span>
                                    </h2>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={handleExportCSV}
                                            disabled={interestedIds.size === 0}
                                            style={{
                                                padding: '0.5rem',
                                                color:
                                                    interestedIds.size > 0
                                                        ? colors.textSecondary
                                                        : colors.textMuted,
                                                background: 'transparent',
                                                border: 'none',
                                                cursor:
                                                    interestedIds.size > 0
                                                        ? 'pointer'
                                                        : 'not-allowed',
                                                opacity: interestedIds.size > 0 ? 1 : 0.5,
                                            }}
                                            title={
                                                interestedIds.size > 0
                                                    ? `Export ${interestedIds.size} interested properties to CSV`
                                                    : 'Star properties to enable export'
                                            }
                                        >
                                            <Download className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={handleExportPDF}
                                            disabled={interestedIds.size === 0}
                                            style={{
                                                padding: '0.5rem',
                                                color:
                                                    interestedIds.size > 0
                                                        ? colors.textSecondary
                                                        : colors.textMuted,
                                                background: 'transparent',
                                                border: 'none',
                                                cursor:
                                                    interestedIds.size > 0
                                                        ? 'pointer'
                                                        : 'not-allowed',
                                                opacity: interestedIds.size > 0 ? 1 : 0.5,
                                            }}
                                            title={
                                                interestedIds.size > 0
                                                    ? `Export ${interestedIds.size} interested properties to PDF tear sheet`
                                                    : 'Star properties to enable PDF export'
                                            }
                                        >
                                            <FileText className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* All Results / Interested / Dismissed Toggle */}
                                <div
                                    style={{
                                        display: 'flex',
                                        marginBottom: '0.75rem',
                                        background: colors.backgroundLight,
                                        borderRadius: '0.5rem',
                                        padding: '0.25rem',
                                        border: `1px solid ${colors.border}`,
                                    }}
                                >
                                    <button
                                        onClick={() => {
                                            setShowInterestedOnly(false);
                                            setShowDismissedOnly(false);
                                        }}
                                        style={{
                                            flex: 1,
                                            padding: '0.5rem 0.5rem',
                                            fontSize: '0.7rem',
                                            fontWeight: 500,
                                            borderRadius: '0.375rem',
                                            border: 'none',
                                            cursor: 'pointer',
                                            background:
                                                !showInterestedOnly && !showDismissedOnly
                                                    ? colors.accent
                                                    : 'transparent',
                                            color:
                                                !showInterestedOnly && !showDismissedOnly
                                                    ? '#fff'
                                                    : colors.textMuted,
                                            transition: 'all 150ms ease',
                                        }}
                                    >
                                        All
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowInterestedOnly(true);
                                            setShowDismissedOnly(false);
                                        }}
                                        style={{
                                            flex: 1,
                                            padding: '0.5rem 0.5rem',
                                            fontSize: '0.7rem',
                                            fontWeight: 500,
                                            borderRadius: '0.375rem',
                                            border: 'none',
                                            cursor: 'pointer',
                                            background: showInterestedOnly
                                                ? '#F59E0B'
                                                : 'transparent',
                                            color: showInterestedOnly ? '#fff' : colors.textMuted,
                                            transition: 'all 150ms ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '0.25rem',
                                        }}
                                    >
                                        <Star
                                            style={{
                                                width: '11px',
                                                height: '11px',
                                                fill: showInterestedOnly ? '#fff' : 'none',
                                            }}
                                        />
                                        {interestedIds.size}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowInterestedOnly(false);
                                            setShowDismissedOnly(true);
                                        }}
                                        style={{
                                            flex: 1,
                                            padding: '0.5rem 0.5rem',
                                            fontSize: '0.7rem',
                                            fontWeight: 500,
                                            borderRadius: '0.375rem',
                                            border: 'none',
                                            cursor: 'pointer',
                                            background: showDismissedOnly
                                                ? colors.error
                                                : 'transparent',
                                            color: showDismissedOnly ? '#fff' : colors.textMuted,
                                            transition: 'all 150ms ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '0.25rem',
                                        }}
                                    >
                                        <EyeOff
                                            style={{
                                                width: '11px',
                                                height: '11px',
                                            }}
                                        />
                                        {dismissedIds.size}
                                    </button>
                                </div>

                                <select
                                    value={sortBy}
                                    onChange={e => setSortBy(e.target.value as SortOption)}
                                    style={{
                                        width: '100%',
                                        background: colors.background,
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '0.5rem',
                                        padding: '0.5rem',
                                        color: colors.textPrimary,
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    <option value="auction_date">Sort by Auction Date</option>
                                    <option value="opening_bid_asc">
                                        Opening Bid: Low to High
                                    </option>
                                    <option value="opening_bid_desc">
                                        Opening Bid: High to Low
                                    </option>
                                </select>
                            </div>

                            {/* Property List */}
                            <div
                                style={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    padding: '1rem',
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                }}
                                className="space-y-3 hide-scrollbar"
                            >
                                {sortedProperties.length === 0 ? (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '2rem 1rem',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <img
                                            src="/assets/realsignal-logo-solo.png"
                                            alt="RealSignal"
                                            style={{
                                                width: '2.5rem',
                                                height: '2.5rem',
                                                opacity: 0.5,
                                                marginBottom: '0.75rem',
                                            }}
                                        />
                                        {showInterestedOnly ? (
                                            <>
                                                <p
                                                    style={{
                                                        fontSize: '0.875rem',
                                                        color: colors.textSecondary,
                                                        marginBottom: '0.25rem',
                                                    }}
                                                >
                                                    Star properties on cards to add
                                                </p>
                                                <p
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        color: colors.textMuted,
                                                    }}
                                                >
                                                    No properties selected
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p
                                                    style={{
                                                        fontSize: '0.875rem',
                                                        color: colors.textSecondary,
                                                        marginBottom: '0.25rem',
                                                    }}
                                                >
                                                    No properties match your criteria
                                                </p>
                                                <p
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        color: colors.textMuted,
                                                    }}
                                                >
                                                    Try adjusting your filters
                                                </p>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    paginatedProperties.map(property => (
                                        <PropertyCard
                                            key={property.id}
                                            property={property}
                                            isSelected={property.id === selectedPropertyId}
                                            isStarred={interestedIds.has(property.id)}
                                            isDismissed={dismissedIds.has(property.id)}
                                            onClick={() => handleCardClick(property)}
                                            onToggleStar={handleToggleStar}
                                            onDismiss={handleDismiss}
                                            onRestore={handleRestore}
                                        />
                                    ))
                                )}
                            </div>

                            {/* Pagination Controls */}
                            {sortedProperties.length > 0 && totalPages > 1 && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.75rem',
                                        padding: '0.75rem 1rem',
                                        borderTop: `1px solid ${colors.border}`,
                                        background: colors.backgroundCard,
                                    }}
                                >
                                    <button
                                        onClick={() =>
                                            setCurrentPage(prev => Math.max(1, prev - 1))
                                        }
                                        disabled={currentPage === 1}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '2rem',
                                            height: '2rem',
                                            borderRadius: '0.375rem',
                                            border: `1px solid ${colors.border}`,
                                            background:
                                                currentPage === 1
                                                    ? 'transparent'
                                                    : colors.background,
                                            color:
                                                currentPage === 1
                                                    ? colors.textMuted
                                                    : colors.textSecondary,
                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                            opacity: currentPage === 1 ? 0.5 : 1,
                                        }}
                                        aria-label="Previous page"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <span
                                        style={{
                                            fontSize: '0.75rem',
                                            color: colors.textSecondary,
                                            minWidth: '5rem',
                                            textAlign: 'center',
                                        }}
                                    >
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() =>
                                            setCurrentPage(prev => Math.min(totalPages, prev + 1))
                                        }
                                        disabled={currentPage === totalPages}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '2rem',
                                            height: '2rem',
                                            borderRadius: '0.375rem',
                                            border: `1px solid ${colors.border}`,
                                            background:
                                                currentPage === totalPages
                                                    ? 'transparent'
                                                    : colors.background,
                                            color:
                                                currentPage === totalPages
                                                    ? colors.textMuted
                                                    : colors.textSecondary,
                                            cursor:
                                                currentPage === totalPages
                                                    ? 'not-allowed'
                                                    : 'pointer',
                                            opacity: currentPage === totalPages ? 0.5 : 1,
                                        }}
                                        aria-label="Next page"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </aside>
                    </>
                )}
            </div>

            {/* Property Detail Drawer */}
            {selectedProperty && (
                <PropertyDetailDrawer
                    property={selectedProperty}
                    isStarred={interestedIds.has(selectedProperty.id)}
                    onClose={() => setSelectedPropertyId(null)}
                    onToggleStar={handleToggleStar}
                    onExportProperty={handleExportSingleProperty}
                    onExportPropertyCSV={handleExportSinglePropertyCSV}
                    enrichmentFields={enrichmentCache[selectedProperty.id]}
                    enrichmentLoading={enrichmentLoading}
                    enrichmentRequesting={enrichmentRequesting}
                    onRequestEnrichment={requestEnrichment}
                    enrichmentTablesInitialized={enrichmentTablesInitialized}
                />
            )}

            {/* Searched Location Drawer */}
            {searchedLocation && !selectedProperty && (
                <div
                    style={{
                        position: 'fixed',
                        top: '64px',
                        right: 0,
                        width: '420px',
                        height: 'calc(100vh - 64px)',
                        background: colors.backgroundLight,
                        borderLeft: `1px solid ${colors.border}`,
                        boxShadow: '-10px 0 40px rgba(0, 0, 0, 0.3)',
                        zIndex: 100,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div
                        style={{
                            padding: '1rem',
                            borderBottom: `1px solid ${colors.border}`,
                        }}
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <h3
                                    style={{
                                        fontSize: '1.125rem',
                                        fontWeight: 600,
                                        color: colors.textPrimary,
                                    }}
                                >
                                    Search Result
                                </h3>
                                <p style={{ fontSize: '0.875rem', color: colors.textMuted }}>
                                    {searchedLocation.address}
                                </p>
                            </div>
                            <button
                                onClick={() => setSearchedLocation(null)}
                                style={{
                                    padding: '0.5rem',
                                    color: colors.textMuted,
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
                        <div className="space-y-4">
                            <StreetViewPanorama
                                lat={searchedLocation.lat}
                                lng={searchedLocation.lng}
                                isHighConfidence={true}
                            />

                            <div
                                style={{
                                    background: colors.background,
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '0.5rem',
                                    padding: '1rem',
                                }}
                            >
                                <p
                                    style={{
                                        fontSize: '0.75rem',
                                        color: colors.textMuted,
                                        marginBottom: '0.5rem',
                                    }}
                                >
                                    Coordinates
                                </p>
                                <p style={{ fontSize: '0.875rem', color: colors.textPrimary }}>
                                    {searchedLocation.lat.toFixed(6)},{' '}
                                    {searchedLocation.lng.toFixed(6)}
                                </p>
                            </div>

                            <p
                                style={{
                                    fontSize: '0.6875rem',
                                    color: colors.textMuted,
                                    fontStyle: 'italic',
                                }}
                            >
                                Not in database
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Settings Drawer */}
            <SettingsDrawer
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                userEmail={userProfile?.email || 'Not signed in'}
                userName={userProfile?.name || 'Guest'}
                subscriptionStatus={userProfile?.subscriptionStatus || 'expired'}
                subscriptionEndDate={userProfile?.subscriptionEndDate || undefined}
            />
        </div>
    );
};

interface FilterSectionProps {
    title: React.ReactNode;
    children: React.ReactNode;
    defaultOpen?: boolean;
    isAdvanced?: boolean;
}

const FilterSection: React.FC<FilterSectionProps> = ({
    title,
    children,
    defaultOpen = true,
    isAdvanced = false,
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div
            style={{
                marginTop: '0.5rem',
                marginBottom: '0.5rem',
                background: colors.backgroundWell,
                borderRadius: '0.5rem',
                borderLeft: `2px solid ${isAdvanced ? 'rgba(127, 140, 156, 0.3)' : 'rgba(58, 106, 122, 0.5)'}`,
                overflow: 'hidden',
            }}
        >
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between w-full text-left"
                style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.75rem 1rem',
                }}
            >
                <span
                    style={{
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: isAdvanced ? colors.textMuted : colors.textSecondary,
                    }}
                >
                    {title}
                </span>
                {isOpen ? (
                    <ChevronUpIcon
                        style={{ width: '14px', height: '14px', color: colors.textMuted }}
                    />
                ) : (
                    <ChevronDownIcon
                        style={{ width: '14px', height: '14px', color: colors.textMuted }}
                    />
                )}
            </button>
            {isOpen && <div style={{ padding: '0 1rem 0.75rem 1rem' }}>{children}</div>}
        </div>
    );
};

interface PropertyCardProps {
    property: Property;
    isSelected: boolean;
    isStarred: boolean;
    isDismissed: boolean;
    onClick: () => void;
    onToggleStar: (propertyId: string, e?: React.MouseEvent) => void;
    onDismiss: (propertyId: string, e?: React.MouseEvent) => void;
    onRestore: (propertyId: string, e?: React.MouseEvent) => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
    property,
    isSelected,
    isStarred,
    isDismissed,
    onClick,
    onToggleStar,
    onDismiss,
    onRestore,
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const hasCriteriaMatches = property.criteriaMatches && property.criteriaMatches.length > 0;

    // Status badge color mapping
    const getStatusStyle = (status: string | undefined) => {
        switch (status) {
            case 'Scheduled':
                return { background: 'rgba(34, 197, 94, 0.2)', color: '#22C55E' }; // Green
            case 'Postponed':
                return { background: 'rgba(234, 179, 8, 0.2)', color: '#EAB308' }; // Yellow
            case 'Cancelled':
                return { background: 'rgba(239, 68, 68, 0.2)', color: '#EF4444' }; // Red
            case 'Sold':
                return { background: 'rgba(59, 130, 246, 0.2)', color: '#3B82F6' }; // Blue
            default:
                return { background: 'rgba(127, 140, 156, 0.2)', color: colors.textMuted };
        }
    };

    const statusStyle = getStatusStyle(property.status);

    // Check if we have parcel inventory data (only show if exists)
    const hasMarketValue = property.marketValue !== undefined && property.marketValue !== null;
    const hasAssessedValue =
        property.assessedValue !== undefined && property.assessedValue !== null;
    const hasYearBuilt = property.yearBuilt !== undefined && property.yearBuilt !== null;
    const hasParcelData = hasMarketValue || hasAssessedValue || hasYearBuilt;

    return (
        <div
            onClick={onClick}
            style={{
                background: isSelected ? `rgba(58, 106, 122, 0.08)` : colors.backgroundCard,
                border: `1px solid ${isSelected ? colors.accent : 'rgba(255, 255, 255, 0.06)'}`,
                borderRadius: '0.5rem',
                padding: '0.875rem',
                cursor: 'pointer',
                transition: 'all 150ms ease',
                boxShadow: isSelected
                    ? `0 0 12px 2px rgba(58, 106, 122, 0.4), 0 0 20px 4px rgba(58, 106, 122, 0.2)`
                    : '0 4px 12px rgba(0, 0, 0, 0.25), 0 2px 4px rgba(0, 0, 0, 0.15)',
            }}
        >
            {/* Address Row with Star Toggle */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '0.5rem',
                }}
            >
                <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                        style={{
                            color: colors.textPrimary,
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {property.address}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: colors.textMuted }}>
                        {property.city}, {property.state} {property.zip}
                    </p>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.125rem',
                        marginLeft: '0.5rem',
                    }}
                >
                    <button
                        onClick={e =>
                            isDismissed ? onRestore(property.id, e) : onDismiss(property.id, e)
                        }
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '0.25rem',
                            transition: 'all 150ms ease',
                        }}
                        title={isDismissed ? 'Restore property' : 'Dismiss property'}
                    >
                        {isDismissed ? (
                            <Eye
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    color: colors.error,
                                    transition: 'all 150ms ease',
                                }}
                            />
                        ) : (
                            <EyeOff
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    color: colors.textMuted,
                                    transition: 'all 150ms ease',
                                }}
                            />
                        )}
                    </button>
                    <button
                        onClick={e => onToggleStar(property.id, e)}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '0.25rem',
                            transition: 'all 150ms ease',
                        }}
                        title={isStarred ? 'Remove from watchlist' : 'Add to watchlist'}
                    >
                        <Star
                            style={{
                                width: '18px',
                                height: '18px',
                                color: isStarred ? '#F59E0B' : colors.textMuted,
                                fill: isStarred ? '#F59E0B' : 'none',
                                transition: 'all 150ms ease',
                            }}
                        />
                    </button>
                </div>
            </div>

            {/* Status Badge Row - Prominent distressed event context */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.75rem',
                    flexWrap: 'wrap',
                }}
            >
                {/* Status Badge - Primary signal */}
                {property.status && (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.02em',
                            ...statusStyle,
                        }}
                    >
                        {property.status}
                    </span>
                )}
                {/* Location accuracy badge - user-facing labels */}
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '0.1875rem',
                        fontSize: '0.625rem',
                        fontWeight: 500,
                        background: `rgba(28, 46, 66, 1)`,
                        color: colors.textMuted,
                    }}
                    title={
                        property.locationType === 'parcel_polygon' ||
                        property.locationType === 'rooftop'
                            ? 'Location is precise.'
                            : 'Location is estimated. Verify before acting.'
                    }
                >
                    {property.locationType === 'parcel_polygon' ||
                    property.locationType === 'rooftop'
                        ? 'Precise location'
                        : 'Map location estimated'}
                </span>
            </div>

            {/* Physical Viability Row - Beds/Baths/SqFt/Type (V1 Core) */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.75rem',
                    marginBottom: '0.5rem',
                    color: colors.textSecondary,
                    flexWrap: 'wrap',
                }}
            >
                {/* Beds */}
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span style={{ fontWeight: 600 }}>
                        {property.beds !== undefined && property.beds !== null
                            ? property.beds
                            : '—'}
                    </span>
                    <span style={{ color: colors.textMuted, fontSize: '0.6875rem' }}>bed</span>
                </span>
                <span style={{ color: colors.textMuted }}>|</span>
                {/* Baths */}
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span style={{ fontWeight: 600 }}>
                        {property.baths !== undefined && property.baths !== null
                            ? property.baths
                            : '—'}
                    </span>
                    <span style={{ color: colors.textMuted, fontSize: '0.6875rem' }}>bath</span>
                </span>
                <span style={{ color: colors.textMuted }}>|</span>
                {/* Sq Ft */}
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span style={{ fontWeight: 600 }}>
                        {property.sqft !== undefined && property.sqft !== null
                            ? property.sqft.toLocaleString()
                            : '—'}
                    </span>
                    <span style={{ color: colors.textMuted, fontSize: '0.6875rem' }}>sqft</span>
                </span>
                {/* Property Type - only show if available */}
                {property.propertyType && (
                    <>
                        <span style={{ color: colors.textMuted }}>|</span>
                        <span
                            style={{
                                fontSize: '0.6875rem',
                                color: colors.textMuted,
                                textTransform: 'uppercase',
                            }}
                        >
                            {property.propertyType}
                        </span>
                    </>
                )}
            </div>

            {/* Enrichment Status - Show if any physical data is missing */}
            {(property.beds === undefined ||
                property.beds === null ||
                property.baths === undefined ||
                property.baths === null ||
                property.sqft === undefined ||
                property.sqft === null) && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        marginBottom: '0.5rem',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(156, 163, 175, 0.1)',
                        borderRadius: '0.25rem',
                        fontSize: '0.625rem',
                        color: '#9CA3AF',
                    }}
                >
                    <span
                        style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#9CA3AF',
                        }}
                    />
                    Needs enrichment
                </div>
            )}

            {/* Distressed Event Intelligence Grid - Opening Bid & Auction Date */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.75rem',
                    fontSize: '0.75rem',
                    marginBottom: '0.75rem',
                }}
            >
                <div>
                    <p
                        style={{
                            color: colors.textMuted,
                            fontSize: '0.625rem',
                            marginBottom: '0.25rem',
                        }}
                    >
                        Opening Bid
                    </p>
                    <p
                        style={{
                            color: colors.textPrimary,
                            fontWeight: 600,
                            fontSize: '0.9375rem',
                        }}
                    >
                        {property.openingBid !== undefined
                            ? formatCurrency(property.openingBid)
                            : 'TBD'}
                    </p>
                </div>
                <div>
                    <p
                        style={{
                            color: colors.textMuted,
                            fontSize: '0.625rem',
                            marginBottom: '0.25rem',
                        }}
                    >
                        Auction Date
                    </p>
                    <p
                        style={{
                            color: colors.textPrimary,
                            fontWeight: 600,
                            fontSize: '0.9375rem',
                        }}
                    >
                        {property.auctionDate ? formatDate(property.auctionDate) : 'TBD'}
                    </p>
                </div>
            </div>

            {/* Parcel Intelligence Grid - Only show if data exists */}
            {hasParcelData && (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns:
                            hasMarketValue && hasAssessedValue && hasYearBuilt
                                ? '1fr 1fr 1fr'
                                : hasMarketValue || hasAssessedValue
                                  ? '1fr 1fr'
                                  : '1fr',
                        gap: '0.5rem',
                        fontSize: '0.75rem',
                        marginBottom: '0.5rem',
                        paddingTop: '0.5rem',
                        borderTop: `1px solid ${colors.border}`,
                    }}
                >
                    {hasMarketValue && (
                        <div>
                            <p
                                style={{
                                    color: colors.textMuted,
                                    fontSize: '0.625rem',
                                    marginBottom: '0.125rem',
                                }}
                            >
                                Market Value
                            </p>
                            <p style={{ color: colors.textSecondary, fontWeight: 500 }}>
                                {formatCurrency(property.marketValue!)}
                            </p>
                        </div>
                    )}
                    {hasAssessedValue && (
                        <div>
                            <p
                                style={{
                                    color: colors.textMuted,
                                    fontSize: '0.625rem',
                                    marginBottom: '0.125rem',
                                }}
                            >
                                Assessed
                            </p>
                            <p style={{ color: colors.textSecondary, fontWeight: 500 }}>
                                {formatCurrency(property.assessedValue!)}
                            </p>
                        </div>
                    )}
                    {hasYearBuilt && (
                        <div>
                            <p
                                style={{
                                    color: colors.textMuted,
                                    fontSize: '0.625rem',
                                    marginBottom: '0.125rem',
                                }}
                            >
                                Year Built
                            </p>
                            <p style={{ color: colors.textSecondary, fontWeight: 500 }}>
                                {property.yearBuilt}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Owner Row - Secondary context, below distressed fields */}
            {property.ownerName && (
                <div
                    style={{
                        marginTop: hasParcelData ? '0' : '0.5rem',
                        paddingTop: hasParcelData ? '0' : '0.5rem',
                        borderTop: hasParcelData ? 'none' : `1px solid ${colors.border}`,
                        fontSize: '0.6875rem',
                    }}
                >
                    <span style={{ color: colors.textMuted }}>Owner: </span>
                    <span
                        style={{
                            color: colors.textSecondary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {property.ownerName}
                    </span>
                </div>
            )}

            {/* Deal Criteria v0: "Why this appears" - Explainability Section */}
            {hasCriteriaMatches && (
                <div
                    style={{
                        marginTop: '0.75rem',
                        borderTop: `1px solid ${colors.border}`,
                        paddingTop: '0.5rem',
                    }}
                >
                    <button
                        onClick={e => {
                            e.stopPropagation();
                            setShowDetails(!showDetails);
                        }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            color: colors.textMuted,
                            fontSize: '0.6875rem',
                        }}
                    >
                        {showDetails ? (
                            <ChevronUpIcon style={{ width: '12px', height: '12px' }} />
                        ) : (
                            <ChevronDownIcon style={{ width: '12px', height: '12px' }} />
                        )}
                        <span>Why this appears</span>
                    </button>
                    {showDetails && (
                        <div style={{ marginTop: '0.375rem' }}>
                            <p
                                style={{
                                    fontSize: '0.625rem',
                                    color: colors.textMuted,
                                    marginBottom: '0.25rem',
                                }}
                            >
                                Matches your criteria:
                            </p>
                            <ul style={{ margin: 0, paddingLeft: '1rem', listStyleType: 'disc' }}>
                                {property.criteriaMatches.map((match, idx) => (
                                    <li
                                        key={idx}
                                        style={{
                                            fontSize: '0.625rem',
                                            color: colors.textSecondary,
                                            marginBottom: '0.125rem',
                                        }}
                                    >
                                        {match}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

interface PropertyDetailDrawerProps {
    property: Property;
    isStarred: boolean;
    onClose: () => void;
    onToggleStar: (propertyId: string, e?: React.MouseEvent) => void;
    onExportProperty: (property: Property) => void;
    onExportPropertyCSV: (property: Property) => void;
    enrichmentFields?: Record<string, FieldEnrichmentStatus>;
    enrichmentLoading?: boolean;
    enrichmentRequesting?: boolean;
    onRequestEnrichment?: (propertyId: string) => void;
    enrichmentTablesInitialized?: boolean | null;
}

// Field name mapping: UI uses beds/baths, backend uses bedrooms/bathrooms
const UI_TO_BACKEND_FIELD_MAP: Record<string, string> = {
    beds: 'bedrooms',
    baths: 'bathrooms',
    sqft: 'sqft',
    year_built: 'year_built',
    property_type: 'property_type',
};

// Helper to render field value based on enrichment state
// IMPORTANT: Backend state is authoritative - state overrides stale cached values
const renderEnrichmentField = (
    fieldName: string,
    value: string | number | undefined | null,
    enrichmentFields?: Record<string, FieldEnrichmentStatus>,
    formatFn?: (v: number) => string
): { display: string; color: string; showCTA: boolean } => {
    // Map UI field name to backend field name for enrichment state lookup
    const backendFieldName = UI_TO_BACKEND_FIELD_MAP[fieldName] || fieldName;
    const fieldState = enrichmentFields?.[backendFieldName];
    const state = fieldState?.state || 'unknown';
    const hasValue = value !== undefined && value !== null && value !== '';

    // Backend state is authoritative - check state FIRST
    switch (state) {
        case 'retrieved':
            // State says retrieved - show value if we have it, otherwise data integrity issue
            if (hasValue) {
                const displayValue =
                    typeof value === 'number' && formatFn ? formatFn(value) : String(value);
                return { display: displayValue, color: colors.textPrimary, showCTA: false };
            }
            // Retrieved but missing value = data integrity issue, allow re-request
            return { display: 'Retrieved (missing)', color: '#F59E0B', showCTA: true };

        case 'retrieval_in_progress':
            // Always show enriching state, even if we have a stale cached value
            return { display: 'Enriching...', color: '#EAB308', showCTA: false };

        case 'not_published_by_source':
            // Source confirmed field is not available
            return { display: 'Not available', color: '#9CA3AF', showCTA: false };

        case 'source_unreachable':
            // Source is down/unreachable - show error state
            return { display: 'Source unavailable', color: '#EF4444', showCTA: true };

        case 'requires_manual_review':
            // Needs human intervention
            return { display: 'Needs review', color: '#F59E0B', showCTA: false };

        case 'unknown':
        default:
            // No enrichment state yet - show value if we have it from another source
            if (hasValue) {
                const displayValue =
                    typeof value === 'number' && formatFn ? formatFn(value) : String(value);
                return { display: displayValue, color: colors.textPrimary, showCTA: false };
            }
            // No value and unknown state - show CTA
            return { display: '—', color: '#9CA3AF', showCTA: true };
    }
};

const PropertyDetailDrawer: React.FC<PropertyDetailDrawerProps> = ({
    property,
    isStarred,
    onClose,
    onToggleStar,
    onExportProperty,
    onExportPropertyCSV,
    enrichmentFields,
    enrichmentLoading,
    enrichmentRequesting,
    onRequestEnrichment,
    enrichmentTablesInitialized,
}) => {
    // v2 Add-on aligned tabs: Overview (Core), Execution (Execution Intelligence Pack), Tax (Tax Lien & Redemption Pack), Workflow (Portfolio & Workflow Pro)
    type DrawerTab = 'overview' | 'execution' | 'tax' | 'workflow';
    const [activeTab, setActiveTab] = useState<DrawerTab>('overview');

    // Execution Intelligence state
    const [executionData, setExecutionData] = useState<ExecutionSignalsResponseV1 | null>(null);
    const [executionLoading, setExecutionLoading] = useState(false);
    const [executionError, setExecutionError] = useState<string | null>(null);

    // Tax Intelligence state
    const [taxData, setTaxData] = useState<TaxSignalsResponseV1 | null>(null);
    const [taxLoading, setTaxLoading] = useState(false);
    const [taxError, setTaxError] = useState<string | null>(null);

    // Fetch execution signals when Execution tab is selected
    const fetchExecutionSignals = useCallback(async () => {
        setExecutionLoading(true);
        setExecutionError(null);
        try {
            const result = await realSignalApi.getExecutionSignals(property.id);
            if (result.state === 'success' && result.data) {
                setExecutionData(result.data);
            } else if (result.state === 'authError') {
                setExecutionError(result.error || 'Authentication required');
            } else {
                setExecutionError(result.error || 'Failed to fetch execution signals');
            }
        } catch (err) {
            setExecutionError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setExecutionLoading(false);
        }
    }, [property.id]);

    // Fetch tax signals when Tax tab is selected
    const fetchTaxSignals = useCallback(async () => {
        setTaxLoading(true);
        setTaxError(null);
        try {
            const result = await realSignalApi.getTaxSignals(property.id);
            if (result.state === 'success' && result.data) {
                setTaxData(result.data);
            } else if (result.state === 'authError') {
                setTaxError(result.error || 'Authentication required');
            } else {
                setTaxError(result.error || 'Failed to fetch tax signals');
            }
        } catch (err) {
            setTaxError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setTaxLoading(false);
        }
    }, [property.id]);

    // Reset execution state when property changes
    useEffect(() => {
        setExecutionData(null);
        setExecutionError(null);
    }, [property.id]);

    // Reset tax state when property changes
    useEffect(() => {
        setTaxData(null);
        setTaxError(null);
    }, [property.id]);

    // Fetch when switching to execution tab
    useEffect(() => {
        if (activeTab === 'execution' && !executionData && !executionLoading) {
            fetchExecutionSignals();
        }
    }, [activeTab, executionData, executionLoading, fetchExecutionSignals]);

    // Fetch when switching to tax tab
    useEffect(() => {
        if (activeTab === 'tax' && !taxData && !taxLoading && !taxError) {
            fetchTaxSignals();
        }
    }, [activeTab, taxData, taxLoading, taxError, fetchTaxSignals]);

    return (
        <div
            style={{
                position: 'fixed',
                top: '64px',
                right: 0,
                width: '420px',
                height: 'calc(100vh - 64px)',
                background: colors.backgroundLight,
                borderLeft: `1px solid ${colors.border}`,
                boxShadow: '-10px 0 40px rgba(0, 0, 0, 0.3)',
                zIndex: 100,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Header */}
            <div
                style={{
                    padding: '1rem',
                    borderBottom: `1px solid ${colors.border}`,
                }}
            >
                <div className="flex items-start justify-between">
                    <div style={{ flex: 1 }}>
                        <h3
                            style={{
                                fontSize: '1.125rem',
                                fontWeight: 600,
                                color: colors.textPrimary,
                            }}
                        >
                            {property.address}
                        </h3>
                        <p style={{ fontSize: '0.875rem', color: colors.textMuted }}>
                            {property.city}, {property.state} {property.zip}
                        </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <button
                            onClick={e => onToggleStar(property.id, e)}
                            style={{
                                padding: '0.5rem',
                                color: isStarred ? '#F59E0B' : colors.textMuted,
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            title={isStarred ? 'Remove from watchlist' : 'Add to watchlist'}
                        >
                            <Star
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    fill: isStarred ? '#F59E0B' : 'none',
                                }}
                            />
                        </button>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '0.5rem',
                                color: colors.textMuted,
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div
                style={{
                    display: 'flex',
                    borderBottom: `1px solid ${colors.border}`,
                }}
            >
                {(['overview', 'execution', 'tax', 'workflow'] as const).map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        style={{
                            padding: '0.75rem 1rem',
                            color: activeTab === tab ? colors.accentLight : colors.textMuted,
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            background: 'transparent',
                            border: 'none',
                            borderBottom: `2px solid ${activeTab === tab ? colors.accent : 'transparent'}`,
                            cursor: 'pointer',
                            textTransform: 'capitalize',
                        }}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
                {activeTab === 'overview' && (
                    <div className="space-y-4">
                        {/* Street View */}
                        <StreetViewPanorama
                            lat={property.lat}
                            lng={property.lng}
                            isHighConfidence={property.locationConfidence === 'high'}
                        />

                        {/* Physical Viability Grid - V1 Core with Enrichment State */}
                        <div
                            style={{
                                background: colors.backgroundCard,
                                borderRadius: '0.5rem',
                                padding: '0.75rem',
                                marginBottom: '0.75rem',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '0.5rem',
                                }}
                            >
                                <p
                                    style={{
                                        fontSize: '0.6875rem',
                                        color: colors.textMuted,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    Property Details
                                </p>
                                {enrichmentLoading && (
                                    <span style={{ fontSize: '0.625rem', color: colors.textMuted }}>
                                        <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                                        Loading...
                                    </span>
                                )}
                            </div>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(4, 1fr)',
                                    gap: '0.5rem',
                                }}
                            >
                                {(() => {
                                    const bedsField = renderEnrichmentField(
                                        'beds',
                                        property.beds,
                                        enrichmentFields
                                    );
                                    return (
                                        <div>
                                            <p
                                                style={{
                                                    fontSize: '0.625rem',
                                                    color: colors.textMuted,
                                                }}
                                            >
                                                Beds
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600,
                                                    color: bedsField.color,
                                                }}
                                            >
                                                {bedsField.display}
                                            </p>
                                        </div>
                                    );
                                })()}
                                {(() => {
                                    const bathsField = renderEnrichmentField(
                                        'baths',
                                        property.baths,
                                        enrichmentFields
                                    );
                                    return (
                                        <div>
                                            <p
                                                style={{
                                                    fontSize: '0.625rem',
                                                    color: colors.textMuted,
                                                }}
                                            >
                                                Baths
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600,
                                                    color: bathsField.color,
                                                }}
                                            >
                                                {bathsField.display}
                                            </p>
                                        </div>
                                    );
                                })()}
                                {(() => {
                                    const sqftField = renderEnrichmentField(
                                        'sqft',
                                        property.sqft,
                                        enrichmentFields,
                                        v => v.toLocaleString()
                                    );
                                    return (
                                        <div>
                                            <p
                                                style={{
                                                    fontSize: '0.625rem',
                                                    color: colors.textMuted,
                                                }}
                                            >
                                                Sq Ft
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600,
                                                    color: sqftField.color,
                                                }}
                                            >
                                                {sqftField.display}
                                            </p>
                                        </div>
                                    );
                                })()}
                                {(() => {
                                    const typeField = renderEnrichmentField(
                                        'property_type',
                                        property.propertyType,
                                        enrichmentFields
                                    );
                                    return (
                                        <div>
                                            <p
                                                style={{
                                                    fontSize: '0.625rem',
                                                    color: colors.textMuted,
                                                }}
                                            >
                                                Type
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600,
                                                    color: typeField.color,
                                                }}
                                            >
                                                {typeField.display}
                                            </p>
                                        </div>
                                    );
                                })()}
                            </div>
                            {/* Request Enrichment CTA - shows when any field needs enrichment */}
                            {onRequestEnrichment &&
                                !enrichmentRequesting &&
                                enrichmentFields &&
                                (() => {
                                    // Use module-level UI_TO_BACKEND_FIELD_MAP for field name mapping
                                    const needsEnrichment = [
                                        'beds',
                                        'baths',
                                        'sqft',
                                        'property_type',
                                    ].some(uiField => {
                                        const backendField = UI_TO_BACKEND_FIELD_MAP[uiField];
                                        const fieldState = enrichmentFields[backendField];
                                        return (
                                            (!fieldState || fieldState.state === 'unknown') &&
                                            (uiField === 'beds'
                                                ? property.beds === undefined
                                                : uiField === 'baths'
                                                  ? property.baths === undefined
                                                  : uiField === 'sqft'
                                                    ? property.sqft === undefined
                                                    : !property.propertyType)
                                        );
                                    });
                                    if (!needsEnrichment) return null;
                                    if (enrichmentTablesInitialized === false) {
                                        return (
                                            <div
                                                style={{
                                                    marginTop: '0.75rem',
                                                    padding: '0.5rem',
                                                    fontSize: '0.75rem',
                                                    color: '#9CA3AF',
                                                    background: 'rgba(156, 163, 175, 0.1)',
                                                    borderRadius: '0.375rem',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                Enrichment system not initialized
                                            </div>
                                        );
                                    }
                                    return (
                                        <button
                                            onClick={() => onRequestEnrichment(property.id)}
                                            style={{
                                                marginTop: '0.75rem',
                                                width: '100%',
                                                padding: '0.5rem',
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                                color: colors.textPrimary,
                                                background: colors.accent,
                                                border: 'none',
                                                borderRadius: '0.375rem',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '0.375rem',
                                            }}
                                        >
                                            <RefreshCw className="w-3 h-3" />
                                            Request Enrichment
                                        </button>
                                    );
                                })()}
                            {enrichmentRequesting && (
                                <div
                                    style={{
                                        marginTop: '0.75rem',
                                        textAlign: 'center',
                                        color: '#EAB308',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                                    Requesting enrichment...
                                </div>
                            )}
                        </div>

                        {/* Distressed Event Metrics Grid */}
                        <div className="grid grid-cols-2 gap-3">
                            {/* Opening Bid - show "Not published" if missing */}
                            <MetricCard
                                label="Opening Bid"
                                value={
                                    property.openingBid !== undefined
                                        ? formatCurrency(property.openingBid)
                                        : 'Not published'
                                }
                                prominent
                            />
                            {/* Auction Date - show "Not published" if missing */}
                            <MetricCard
                                label="Auction Date"
                                value={
                                    property.auctionDate
                                        ? formatDate(property.auctionDate)
                                        : 'Not published'
                                }
                                prominent
                            />
                            {/* County - always show */}
                            <MetricCard label="County" value={property.county || 'Harris'} />
                            {/* Status - show if available */}
                            {property.status && (
                                <MetricCard label="Status" value={property.status} />
                            )}
                        </div>

                        {/* Financial Anchors - Assessed & Market Value */}
                        {(property.assessedValue !== undefined ||
                            property.marketValue !== undefined) && (
                            <div
                                style={{
                                    marginTop: '0.75rem',
                                    background: colors.backgroundCard,
                                    borderRadius: '0.5rem',
                                    padding: '0.75rem',
                                }}
                            >
                                <p
                                    style={{
                                        fontSize: '0.6875rem',
                                        color: colors.textMuted,
                                        marginBottom: '0.5rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    Tax Appraisal
                                </p>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '0.5rem',
                                    }}
                                >
                                    <div>
                                        <p
                                            style={{
                                                fontSize: '0.625rem',
                                                color: colors.textMuted,
                                            }}
                                        >
                                            Assessed Value
                                        </p>
                                        <p
                                            style={{
                                                fontSize: '0.875rem',
                                                fontWeight: 600,
                                                color: colors.textPrimary,
                                            }}
                                        >
                                            {property.assessedValue !== undefined
                                                ? formatCurrency(property.assessedValue)
                                                : 'Enrichment underway'}
                                        </p>
                                    </div>
                                    <div>
                                        <p
                                            style={{
                                                fontSize: '0.625rem',
                                                color: colors.textMuted,
                                            }}
                                        >
                                            Market Value
                                        </p>
                                        <p
                                            style={{
                                                fontSize: '0.875rem',
                                                fontWeight: 600,
                                                color: colors.textPrimary,
                                            }}
                                        >
                                            {property.marketValue !== undefined
                                                ? formatCurrency(property.marketValue)
                                                : 'Enrichment underway'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="space-y-2">
                            <button
                                onClick={() => onExportProperty(property)}
                                style={{
                                    width: '100%',
                                    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
                                    color: 'white',
                                    fontWeight: 600,
                                    padding: '0.75rem',
                                    borderRadius: '0.75rem',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                Export to PDF
                            </button>
                            <button
                                onClick={() => onExportPropertyCSV(property)}
                                style={{
                                    width: '100%',
                                    background: colors.backgroundCard,
                                    color: colors.textPrimary,
                                    fontWeight: 600,
                                    padding: '0.75rem',
                                    borderRadius: '0.75rem',
                                    border: `1px solid ${colors.border}`,
                                    cursor: 'pointer',
                                }}
                            >
                                Export to CSV
                            </button>
                            <button
                                onClick={() => onToggleStar(property.id)}
                                style={{
                                    width: '100%',
                                    background: isStarred ? colors.accent : colors.background,
                                    color: isStarred ? 'white' : colors.textSecondary,
                                    border: `1px solid ${isStarred ? colors.accent : colors.border}`,
                                    padding: '0.75rem',
                                    borderRadius: '0.75rem',
                                    cursor: 'pointer',
                                }}
                            >
                                {isStarred ? 'Remove from Watchlist' : 'Add to Watchlist'}
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'execution' && (
                    <div className="space-y-4">
                        {/* Loading state */}
                        {executionLoading && (
                            <div
                                style={{
                                    background: colors.backgroundCard,
                                    borderRadius: '0.5rem',
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                }}
                            >
                                <Loader2
                                    className="animate-spin"
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        color: colors.accent,
                                        margin: '0 auto 0.75rem',
                                    }}
                                />
                                <p style={{ fontSize: '0.875rem', color: colors.textMuted }}>
                                    Loading execution intelligence...
                                </p>
                            </div>
                        )}

                        {/* Error state (network/auth errors) */}
                        {!executionLoading && executionError && (
                            <div
                                style={{
                                    background: colors.backgroundCard,
                                    borderRadius: '0.5rem',
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                }}
                            >
                                <AlertCircle
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        color: colors.error,
                                        margin: '0 auto 0.75rem',
                                    }}
                                />
                                <p
                                    style={{
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: colors.textPrimary,
                                        marginBottom: '0.5rem',
                                    }}
                                >
                                    Execution Intelligence
                                </p>
                                <p
                                    style={{
                                        fontSize: '0.875rem',
                                        color: colors.textMuted,
                                        marginBottom: '1rem',
                                    }}
                                >
                                    {executionError}
                                </p>
                                <button
                                    onClick={() => {
                                        setExecutionData(null);
                                        setExecutionError(null);
                                        fetchExecutionSignals();
                                    }}
                                    style={{
                                        background: colors.accent,
                                        color: 'white',
                                        padding: '0.5rem 1rem',
                                        borderRadius: '0.375rem',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                    }}
                                >
                                    <RefreshCw style={{ width: '14px', height: '14px' }} />
                                    Try again
                                </button>
                            </div>
                        )}

                        {/* Success states based on readinessStatus */}
                        {!executionLoading && !executionError && executionData && (
                            <>
                                {/* State 1: ready + signals.length === 0 */}
                                {executionData.readinessStatus === 'ready' &&
                                    executionData.signals.length === 0 && (
                                        <div
                                            style={{
                                                background: colors.backgroundCard,
                                                borderRadius: '0.5rem',
                                                padding: '1.5rem',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <p
                                                style={{
                                                    fontSize: '1rem',
                                                    fontWeight: 600,
                                                    color: colors.textPrimary,
                                                    marginBottom: '0.5rem',
                                                }}
                                            >
                                                Execution Intelligence
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.875rem',
                                                    color: colors.textMuted,
                                                    marginBottom: '1rem',
                                                }}
                                            >
                                                No execution signals have been generated yet
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.75rem',
                                                    color: colors.accent,
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                Signals will appear after Phase 5 computation is
                                                enabled
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.625rem',
                                                    color: colors.textMuted,
                                                    marginTop: '1rem',
                                                }}
                                            >
                                                Last checked:{' '}
                                                {new Date(
                                                    executionData.generatedAt
                                                ).toLocaleString()}
                                            </p>
                                        </div>
                                    )}

                                {/* State 2: not_ready */}
                                {executionData.readinessStatus === 'not_ready' && (
                                    <div
                                        style={{
                                            background: colors.backgroundCard,
                                            borderRadius: '0.5rem',
                                            padding: '1.5rem',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <p
                                            style={{
                                                fontSize: '1rem',
                                                fontWeight: 600,
                                                color: colors.textPrimary,
                                                marginBottom: '0.5rem',
                                            }}
                                        >
                                            Execution Intelligence
                                        </p>
                                        <p
                                            style={{
                                                fontSize: '0.875rem',
                                                color: colors.textMuted,
                                                marginBottom: '0.5rem',
                                            }}
                                        >
                                            Execution Intelligence is not available for this
                                            property yet
                                        </p>
                                        {executionData.notes && executionData.notes.length > 0 && (
                                            <p
                                                style={{
                                                    fontSize: '0.75rem',
                                                    color: colors.textMuted,
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                {executionData.notes.join(' ')}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* State 3: unknown (system error) */}
                                {executionData.readinessStatus === 'unknown' && (
                                    <div
                                        style={{
                                            background: colors.backgroundCard,
                                            borderRadius: '0.5rem',
                                            padding: '1.5rem',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <ServerOff
                                            style={{
                                                width: '24px',
                                                height: '24px',
                                                color: colors.warning,
                                                margin: '0 auto 0.75rem',
                                            }}
                                        />
                                        <p
                                            style={{
                                                fontSize: '1rem',
                                                fontWeight: 600,
                                                color: colors.textPrimary,
                                                marginBottom: '0.5rem',
                                            }}
                                        >
                                            Execution Intelligence
                                        </p>
                                        <p
                                            style={{
                                                fontSize: '0.875rem',
                                                color: colors.textMuted,
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            Execution Intelligence status could not be determined
                                        </p>
                                        {executionData.notes && executionData.notes.length > 0 && (
                                            <p
                                                style={{
                                                    fontSize: '0.75rem',
                                                    color: colors.textMuted,
                                                    fontStyle: 'italic',
                                                    marginBottom: '1rem',
                                                }}
                                            >
                                                {executionData.notes.join(' ')}
                                            </p>
                                        )}
                                        <button
                                            onClick={() => {
                                                setExecutionData(null);
                                                fetchExecutionSignals();
                                            }}
                                            style={{
                                                background: colors.accent,
                                                color: 'white',
                                                padding: '0.5rem 1rem',
                                                borderRadius: '0.375rem',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                            }}
                                        >
                                            <RefreshCw style={{ width: '14px', height: '14px' }} />
                                            Try again
                                        </button>
                                    </div>
                                )}

                                {/* State 4: ready + signals exist (future - Phase 5) */}
                                {executionData.readinessStatus === 'ready' &&
                                    executionData.signals.length > 0 && (
                                        <div
                                            style={{
                                                background: colors.backgroundCard,
                                                borderRadius: '0.5rem',
                                                padding: '1.5rem',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <p
                                                style={{
                                                    fontSize: '1rem',
                                                    fontWeight: 600,
                                                    color: colors.textPrimary,
                                                    marginBottom: '0.5rem',
                                                }}
                                            >
                                                Execution Intelligence
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.875rem',
                                                    color: colors.textMuted,
                                                }}
                                            >
                                                {executionData.signals.length} signal
                                                {executionData.signals.length !== 1 ? 's' : ''}{' '}
                                                available
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.75rem',
                                                    color: colors.accent,
                                                    fontStyle: 'italic',
                                                    marginTop: '0.5rem',
                                                }}
                                            >
                                                Signal cards coming in Phase 5
                                            </p>
                                        </div>
                                    )}
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'tax' && (
                    <div className="space-y-4">
                        {/* Loading state */}
                        {taxLoading && (
                            <div
                                style={{
                                    background: colors.backgroundCard,
                                    borderRadius: '0.5rem',
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                }}
                            >
                                <Loader2
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        color: colors.accent,
                                        margin: '0 auto 0.75rem',
                                        animation: 'spin 1s linear infinite',
                                    }}
                                />
                                <p style={{ fontSize: '0.875rem', color: colors.textMuted }}>
                                    Loading tax intelligence...
                                </p>
                            </div>
                        )}

                        {/* Error state (network/auth errors) */}
                        {!taxLoading && taxError && (
                            <div
                                style={{
                                    background: colors.backgroundCard,
                                    borderRadius: '0.5rem',
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                }}
                            >
                                <AlertCircle
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        color: colors.error,
                                        margin: '0 auto 0.75rem',
                                    }}
                                />
                                <p
                                    style={{
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: colors.textPrimary,
                                        marginBottom: '0.5rem',
                                    }}
                                >
                                    Tax Intelligence
                                </p>
                                <p
                                    style={{
                                        fontSize: '0.875rem',
                                        color: colors.textMuted,
                                        marginBottom: '1rem',
                                    }}
                                >
                                    {taxError}
                                </p>
                                <button
                                    onClick={() => {
                                        setTaxData(null);
                                        setTaxError(null);
                                        fetchTaxSignals();
                                    }}
                                    style={{
                                        background: colors.accent,
                                        color: 'white',
                                        padding: '0.5rem 1rem',
                                        borderRadius: '0.375rem',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                    }}
                                >
                                    <RefreshCw style={{ width: '14px', height: '14px' }} />
                                    Try again
                                </button>
                            </div>
                        )}

                        {/* Success states based on readinessStatus */}
                        {!taxLoading && !taxError && taxData && (
                            <>
                                {/* State 1: ready + signals.length === 0 */}
                                {taxData.readinessStatus === 'ready' &&
                                    taxData.signals.length === 0 && (
                                        <div
                                            style={{
                                                background: colors.backgroundCard,
                                                borderRadius: '0.5rem',
                                                padding: '1.5rem',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <p
                                                style={{
                                                    fontSize: '1rem',
                                                    fontWeight: 600,
                                                    color: colors.textPrimary,
                                                    marginBottom: '0.5rem',
                                                }}
                                            >
                                                Tax Intelligence
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.875rem',
                                                    color: colors.textMuted,
                                                    marginBottom: '1rem',
                                                }}
                                            >
                                                No tax signals have been generated yet
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.75rem',
                                                    color: colors.accent,
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                Signals will appear after Phase 5 computation is
                                                enabled
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.625rem',
                                                    color: colors.textMuted,
                                                    marginTop: '1rem',
                                                }}
                                            >
                                                Last checked:{' '}
                                                {new Date(taxData.generatedAt).toLocaleString()}
                                            </p>
                                        </div>
                                    )}

                                {/* State 2: not_ready */}
                                {taxData.readinessStatus === 'not_ready' && (
                                    <div
                                        style={{
                                            background: colors.backgroundCard,
                                            borderRadius: '0.5rem',
                                            padding: '1.5rem',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <p
                                            style={{
                                                fontSize: '1rem',
                                                fontWeight: 600,
                                                color: colors.textPrimary,
                                                marginBottom: '0.5rem',
                                            }}
                                        >
                                            Tax Intelligence
                                        </p>
                                        <p
                                            style={{
                                                fontSize: '0.875rem',
                                                color: colors.textMuted,
                                                marginBottom: '0.5rem',
                                            }}
                                        >
                                            Tax Intelligence is not available for this property yet
                                        </p>
                                        {taxData.notes && taxData.notes.length > 0 && (
                                            <p
                                                style={{
                                                    fontSize: '0.75rem',
                                                    color: colors.textMuted,
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                {taxData.notes.join(' ')}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* State 3: unknown (system error) */}
                                {taxData.readinessStatus === 'unknown' && (
                                    <div
                                        style={{
                                            background: colors.backgroundCard,
                                            borderRadius: '0.5rem',
                                            padding: '1.5rem',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <ServerOff
                                            style={{
                                                width: '24px',
                                                height: '24px',
                                                color: colors.warning,
                                                margin: '0 auto 0.75rem',
                                            }}
                                        />
                                        <p
                                            style={{
                                                fontSize: '1rem',
                                                fontWeight: 600,
                                                color: colors.textPrimary,
                                                marginBottom: '0.5rem',
                                            }}
                                        >
                                            Tax Intelligence
                                        </p>
                                        <p
                                            style={{
                                                fontSize: '0.875rem',
                                                color: colors.textMuted,
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            Tax Intelligence status could not be determined
                                        </p>
                                        {taxData.notes && taxData.notes.length > 0 && (
                                            <p
                                                style={{
                                                    fontSize: '0.75rem',
                                                    color: colors.textMuted,
                                                    fontStyle: 'italic',
                                                    marginBottom: '1rem',
                                                }}
                                            >
                                                {taxData.notes.join(' ')}
                                            </p>
                                        )}
                                        <button
                                            onClick={() => {
                                                setTaxData(null);
                                                fetchTaxSignals();
                                            }}
                                            style={{
                                                background: colors.accent,
                                                color: 'white',
                                                padding: '0.5rem 1rem',
                                                borderRadius: '0.375rem',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                            }}
                                        >
                                            <RefreshCw style={{ width: '14px', height: '14px' }} />
                                            Try again
                                        </button>
                                    </div>
                                )}

                                {/* State 4: ready + signals exist */}
                                {taxData.readinessStatus === 'ready' &&
                                    taxData.signals.length > 0 && (
                                        <div
                                            style={{
                                                background: colors.backgroundCard,
                                                borderRadius: '0.5rem',
                                                padding: '1.5rem',
                                            }}
                                        >
                                            <p
                                                style={{
                                                    fontSize: '1rem',
                                                    fontWeight: 600,
                                                    color: colors.textPrimary,
                                                    marginBottom: '0.5rem',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                Tax Intelligence
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.875rem',
                                                    color: colors.textMuted,
                                                    textAlign: 'center',
                                                    marginBottom: '1rem',
                                                }}
                                            >
                                                {taxData.signals.length} signal
                                                {taxData.signals.length !== 1 ? 's' : ''} available
                                            </p>
                                            {/* Signal titles list */}
                                            <div
                                                style={{
                                                    borderTop: `1px solid ${colors.border}`,
                                                    paddingTop: '1rem',
                                                }}
                                            >
                                                {taxData.signals.map((signal, index) => (
                                                    <div
                                                        key={signal.signalId || index}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            gap: '0.5rem',
                                                            padding: '0.5rem 0',
                                                            borderBottom:
                                                                index < taxData.signals.length - 1
                                                                    ? `1px solid ${colors.border}`
                                                                    : 'none',
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600,
                                                                color:
                                                                    signal.severity === 'blocker'
                                                                        ? colors.warning
                                                                        : signal.severity ===
                                                                            'action'
                                                                          ? colors.accent
                                                                          : colors.textMuted,
                                                                textTransform: 'uppercase',
                                                                minWidth: '60px',
                                                            }}
                                                        >
                                                            {signal.severity}
                                                        </span>
                                                        <div style={{ flex: 1 }}>
                                                            <p
                                                                style={{
                                                                    fontSize: '0.875rem',
                                                                    fontWeight: 500,
                                                                    color: colors.textPrimary,
                                                                    marginBottom: '0.25rem',
                                                                }}
                                                            >
                                                                {signal.title}
                                                            </p>
                                                            <p
                                                                style={{
                                                                    fontSize: '0.75rem',
                                                                    color: colors.textMuted,
                                                                }}
                                                            >
                                                                {signal.summary}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'workflow' && (
                    <div className="space-y-4">
                        <div
                            style={{
                                background: colors.backgroundCard,
                                borderRadius: '0.5rem',
                                padding: '1.5rem',
                                textAlign: 'center',
                            }}
                        >
                            <p
                                style={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    color: colors.textPrimary,
                                    marginBottom: '0.5rem',
                                }}
                            >
                                Portfolio & Workflow Pro
                            </p>
                            <p
                                style={{
                                    fontSize: '0.875rem',
                                    color: colors.textMuted,
                                    marginBottom: '1rem',
                                }}
                            >
                                Pipeline stages, activity timeline, per-property settings, and notes
                            </p>
                            <p
                                style={{
                                    fontSize: '0.75rem',
                                    color: colors.accent,
                                    fontStyle: 'italic',
                                }}
                            >
                                Coming soon
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

interface MetricCardProps {
    label: string;
    value: string;
    prominent?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, prominent = false }) => (
    <div
        style={{
            background: prominent ? colors.backgroundCard : colors.background,
            border: `1px solid ${prominent ? colors.accent : colors.border}`,
            borderRadius: '0.375rem',
            padding: prominent ? '1rem' : '0.75rem',
        }}
    >
        <p style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
            {label}
        </p>
        <p
            style={{
                fontSize: prominent ? '1.25rem' : '1rem',
                fontWeight: prominent ? 700 : 600,
                color: prominent ? colors.textPrimary : colors.textPrimary,
            }}
        >
            {value}
        </p>
    </div>
);

interface StreetViewPanoramaProps {
    lat: number;
    lng: number;
    isHighConfidence: boolean;
}

const StreetViewPanorama: React.FC<StreetViewPanoramaProps> = ({ lat, lng, isHighConfidence }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [status, setStatus] = useState<'loading' | 'available' | 'unavailable'>('loading');
    const panoramaRef = useRef<google.maps.StreetViewPanorama | null>(null);

    useEffect(() => {
        if (!isHighConfidence) {
            return;
        }

        if (!window.google || !window.google.maps) {
            setStatus('unavailable');
            return;
        }

        const streetViewService = new google.maps.StreetViewService();
        const location = new google.maps.LatLng(lat, lng);

        streetViewService.getPanorama({ location, radius: 1000 }, (data, panoStatus) => {
            if (panoStatus === google.maps.StreetViewStatus.OK && data && containerRef.current) {
                setStatus('available');
                if (!panoramaRef.current) {
                    panoramaRef.current = new google.maps.StreetViewPanorama(containerRef.current, {
                        pano: data.location?.pano,
                        pov: { heading: 0, pitch: 0 },
                        zoom: 1,
                        addressControl: false,
                        showRoadLabels: false,
                        motionTracking: false,
                        motionTrackingControl: false,
                    });
                } else {
                    panoramaRef.current.setPano(data.location?.pano || '');
                }
            } else {
                setStatus('unavailable');
            }
        });

        return () => {
            panoramaRef.current = null;
        };
    }, [lat, lng, isHighConfidence]);

    if (!isHighConfidence) {
        return (
            <div
                style={{
                    background: colors.backgroundWell,
                    borderRadius: '0.375rem',
                    height: '180px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <p style={{ color: colors.textMuted, fontSize: '0.75rem' }}>Approximate location</p>
            </div>
        );
    }

    return (
        <div style={{ position: 'relative' }}>
            {/* Always render the container so ref is available */}
            <div
                ref={containerRef}
                style={{
                    width: '100%',
                    height: '180px',
                    borderRadius: '0.375rem',
                    overflow: 'hidden',
                    display: status === 'available' ? 'block' : 'none',
                }}
            />

            {status === 'loading' && (
                <div
                    style={{
                        background: colors.backgroundWell,
                        borderRadius: '0.375rem',
                        height: '180px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <p style={{ color: colors.textMuted, fontSize: '0.75rem' }}>Loading...</p>
                </div>
            )}

            {status === 'unavailable' && (
                <div
                    style={{
                        background: colors.backgroundWell,
                        borderRadius: '0.375rem',
                        height: '180px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <p style={{ color: colors.textMuted, fontSize: '0.75rem' }}>No Street View</p>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
